/**
 * Dünner Client für das FastAPI-Backend.
 *
 * Alle Aufrufe gehen an denselben Origin unter `/api` — in Produktion strippt
 * nginx das Präfix, in der Entwicklung tut das der Vite-Proxy. Der Client
 * braucht deshalb keine Fallunterscheidung und keine Umgebungsvariable.
 */

import { clearToken, getToken } from './session';
import type { TarifDraht } from './planner/ladeplaner';
import type { Belegbilanz, Kopieroptionen } from './kopieren';
// Die Form der Antwort steht bei den Regeln, die sie auswerten, und nicht hier:
// `reisebuch.ts` liest daraus Stand und Lauf des Buchs und muss das ohne
// Browser prüfen können.
import type { Fassung, Reisebuchkoerper, Vorlage } from './reisebuch';
// Dasselbe Muster für die Briefings: Die Formen stehen bei den Regeln, die sie
// auswerten (`briefing.ts` zerlegt den Text in Absätze und setzt den
// zugeklappten Kopf) — und müssen dort ohne Browser prüfbar sein.
import type { Reisebriefing, Tagesbriefing, Warumentwurf } from './briefing';

export interface User {
  id: number;
  email: string | null;
  display_name: string | null;
  /**
   * `admin` oder `nutzer`. Entscheidet, ob die Verwaltung überhaupt verlinkt wird.
   *
   * **Kein Schutz.** Der Server verlangt für `/admin/*` zweierlei zugleich —
   * die Rolle in der Datenbank **und** die Kontonummer in seiner `.env`. Ein
   * Konto kann hier also `admin` stehen haben und trotzdem an jedem
   * Admin-Aufruf einen 403 bekommen. Die Oberfläche darf deshalb aus diesem
   * Feld nur ableiten, was sie **zeigt**, niemals, was jemand **darf**.
   *
   * Optional, weil ein älterer Server das Feld nicht mitschickt; dann gilt
   * „nutzer", und die Verwaltung bleibt unverlinkt.
   */
  rolle?: string;
  /**
   * Die Schalter des Kontos, aufgelöst (`funktionen.aufgeloest` am Server):
   * `planung`, `tagebuch`, `welterbe` und die anderen, je `true` oder
   * `false`. Wie `rolle` **kein Schutz**, sondern die Auskunft, was die
   * Oberfläche zeigt — den Punkt „Welterbe" in der Leiste, die Fläche für
   * ein Konto ohne Planung. Optional, weil ein älterer Server das Feld nicht
   * mitschickt; dann gilt alles als an, was heute an ist.
   */
  funktionen?: Record<string, boolean>;
  /**
   * Die Anzeigedichte aus §7 — „Expertenansicht" in den Profileinstellungen.
   *
   * Sie hängt am **Konto** und nicht an der Reise: Wer die Rohwerte sehen
   * will, will sie in jeder Reise sehen. Das ist der Unterschied zur
   * Reiseart, die von Reise zu Reise wechselt.
   *
   * Optional, weil ein Server ohne die Spalte das Feld nicht mitschickt.
   * Fehlt es, gilt die einfache Fassung — die Vorgabe, die auch am
   * `<html>`-Element steht.
   */
  expertenansicht?: boolean;
  /**
   * Die Standard-Packliste dieses Kontos — die Vorbelegung neuer Reisen.
   *
   * Am Konto und nicht an der Reise, aus demselben Grund wie die
   * Anzeigedichte: Was man einpackt, hängt am Fahrzeug und am Menschen, nicht
   * am Ziel. Die Reise bekommt beim Anlegen eine **Kopie** davon und geht
   * danach ihren eigenen Weg — sonst änderte das Abhaken einer Zeile in Reise
   * 19 die Liste von Reise 20.
   *
   * Optional, weil ein Server ohne die Spalte das Feld nicht mitschickt; dann
   * zeigen die Profileinstellungen an dieser Stelle einen Satz statt eines
   * leeren Editors. `quelle` unterscheidet die eigene Liste von der
   * eingebauten Vorgabe — sonst sähe „noch nie etwas gesagt" aus wie „genau
   * das hier gewollt".
   */
  packliste?: Kontopackliste;
  /**
   * Der Medienplatz dieses Kontos — Kontingent und Verbrauch in MB.
   *
   * Beides schickt `GET /auth/me` seit dem ersten Mehrbenutzer-Betrieb mit;
   * im Typ standen sie bis zum 12.9.2026 nicht, weil keine Fläche sie zeigte.
   * Seit es die Seite „Konto und Zugang" gibt, zeigt eine — und der genauere
   * Stand kommt von `GET /auth/me/speicher` (siehe `Speicherstand`), das
   * zusätzlich nach Sorten aufschlüsselt. Fehlt der Endpunkt, sind diese zwei
   * Zahlen der Rückfall.
   *
   * `medien_limit_mb: null` heißt **kein Kontingent**, nicht „null MB".
   */
  medien_limit_mb?: number | null;
  medien_belegt_mb?: number;
  /**
   * Zuhause — Adresse und Punkt, als Vorschlag für erste und letzte Station.
   *
   * Am Konto und nicht an der Reise: Die eigene Haustür ist keine Eigenschaft
   * einer Tour. `null` heißt „nicht hinterlegt"; optional ist das Feld, weil
   * ein Server ohne die Spalte es nicht mitschickt (dann bleibt der Block auf
   * der Kontoseite leer und sagt es).
   */
  zuhause?: Zuhause | null;
  /**
   * Die Menschen, die wissen sollen, wo man ist — eine **Merkliste**.
   *
   * Hier wird nichts versandt und nichts geteilt: Der Packlistenposten
   * „Standort für Vertrauenspersonen freigegeben" nennt die Namen, damit man
   * vor der Abfahrt daran denkt. Alles andere wäre eine Zusage, die dieses
   * Produkt nicht einlöst.
   */
  vertrauenspersonen?: Vertrauensperson[] | null;
  /**
   * „Für Lotte" — drei Sätze über sich, höchstens 500 Zeichen.
   *
   * Sie gehen als Absatz „Über den Reisenden" in den **dynamischen** Teil des
   * Systemprompts, also hinter das Cache-Präfix. Was hier steht, hört die
   * Begleiterin unterwegs; es steht auf keiner geteilten Seite.
   */
  lotte_notiz?: string | null;
  /**
   * Die Ladekarten dieses Kontos, in Rangfolge (Vertrag § 1.2).
   *
   * Am Konto und nicht am Fahrzeug, und das ist der eigentliche Umbau dieses
   * Pakets: Ein Vertrag mit EnBW gilt für den Menschen, nicht für den Wagen.
   * Die alten `vehicles.tariffs` bleiben stehen, werden aber von keinem
   * Client mehr gelesen — der Server übersetzt sie beim ersten Lesen einmalig
   * in Karten und markiert sie mit `quelle: 'uebernommen'`.
   *
   * `null` heißt **noch nie etwas gesagt** und ist damit der Auslöser jener
   * Übernahme; eine leere Liste heißt ausdrücklich „keine Karte“, und dann
   * rechnet der Plan überall mit dem Ad-hoc-Richtwert. Optional, weil ein
   * Server ohne die Spalte das Feld nicht mitschickt.
   */
  ladekarten?: Ladekarte[] | null;
  /**
   * Die Reiseprofile dieses Kontos — oder `null` für die Vorlagen des Servers.
   *
   * `null` ist hier keine Leere, sondern eine Auskunft: Es gilt, was der
   * Server mitbringt (`reiseprofile.VORLAGEN`, vier Stück). Die Seite zeigt
   * sie mit der Marke „Vorlage“, und die erste eigene Änderung macht daraus
   * eine eigene Liste — dieselbe Lesart wie bei `packliste`.
   */
  reiseprofile?: Reiseprofil[] | null;
}

/** Zuhause, wie es in `users.zuhause` steht. */
export interface Zuhause {
  adresse: string | null;
  lat: number | null;
  lon: number | null;
}

/** Ein Eintrag der Vertrauenspersonen-Merkliste. */
export interface Vertrauensperson {
  /** Stabil über das Speichern hinweg — der Griff zum Ändern und Streichen. */
  id: string;
  name: string;
  /** Telefon, E-Mail, „über Susanne" — freier Text, nichts wird daraus gewählt. */
  kontakt: string | null;
  /** Soll diese Person den Standort bekommen? Reine Merkmarke, kein Versand. */
  standort: boolean;
}

/**
 * Der Medienplatz, wie `GET /auth/me/speicher` ihn aufschlüsselt.
 *
 * Ein eigener Endpunkt und nicht zwei Zahlen an `/auth/me`, weil die Summe
 * über die Medien läuft und nicht in der Kontozeile steht. Ein 404 heißt:
 * Dieser Server kennt ihn noch nicht — dann zeigt die Kontoseite die zwei
 * Zahlen aus `/auth/me` und sagt, dass die Aufschlüsselung fehlt.
 */
export interface Speicherstand {
  belegt_mb: number;
  /** `null` heißt **kein Kontingent** und nicht „null MB". */
  limit_mb: number | null;
  /** Anzahl der Medien, wenn der Server sie mitzählt. */
  medien?: number | null;
  /** Je Sorte belegte MB (`bild`, `video`, `audio` …), wenn vorhanden. */
  je_art?: Record<string, number> | null;
}

export interface Health {
  status: string;
  version: string;
  apple_web: boolean;
  apple_native?: boolean;
  routing?: string[];
  counts: Record<string, number>;
  /**
   * Der Weg zu einer öffentlich gedachten Beispielreise — für die Anmeldeseite.
   *
   * Er steht in der `.env` des Servers (`BEISPIELREISE_LINK`) und kommt hier
   * als reiner `/geteilt/…`-Pfad an. Kein Geheimnis: Die Freigabe dahinter ist
   * ausdrücklich zum Herzeigen angelegt.
   *
   * Optional und `null`-fähig, und beides heißt dasselbe — es gibt nichts
   * vorzuzeigen: Ein Server ohne die Zeile lässt das Feld auf `null`, ein
   * älterer schickt es gar nicht. Die Anmeldeseite zeigt den Verweis dann
   * nicht, statt ins Leere zu führen.
   */
  beispielreise_link?: string | null;
}

/* ══ Verwaltung ══════════════════════════════════════════════════════════
 *
 * Die Formen hinter `/api/admin/*`. Sie stehen hier und nicht in der Fläche,
 * weil sie beschreiben, was der **Server** sagt — und weil die reine Logik in
 * `verwaltung/verwaltung.ts` sie ohne Browser prüfen können muss.
 */

/** Eine Kontozeile, wie die Verwaltung sie sieht. */
export interface AdminNutzer {
  id: number;
  email: string | null;
  display_name: string | null;
  /** `active` oder `suspended`. */
  status: string | null;
  rolle: string | null;
  notiz: string | null;
  created_at: string | null;
  last_login_at: string | null;
  reisen: number;
  /** Offene, noch gültige Sitzungen. */
  sitzungen: number;
  medien_limit_mb: number | null;
  medien_belegt_mb: number;
  expertenansicht?: boolean;
  /**
   * Die Schalter **aufgelöst** — jeder Name mit einem echten ja/nein.
   *
   * Das ist der Stand, der gilt. Was daneben wirklich am Konto steht, ist
   * `funktionen_gesetzt`: Daran unterscheidet sich „ausdrücklich aus" von „nie
   * etwas dazu gesagt", und genau das will man sehen, bevor man einen Schalter
   * umlegt.
   */
  funktionen: Record<string, boolean>;
  /** Das rohe JSON aus der Spalte. Fehlt ein Name, gilt für ihn die Vorgabe. */
  funktionen_gesetzt: Record<string, boolean>;
}

/** Eine offene Sitzung. Ohne Token-Hash — der beantwortet keine Frage, die man hier stellt. */
export interface AdminSitzung {
  id: number;
  created_at: string | null;
  last_seen_at: string | null;
  expires_at: string | null;
}

/** Die vier Zustände einer Einladung. Der Server rechnet sie aus, nicht wir. */
export type Einladungszustand = 'offen' | 'eingeloest' | 'abgelaufen' | 'zurueckgenommen';

export interface AdminEinladung {
  id: number;
  name: string | null;
  email: string | null;
  funktionen: Record<string, boolean> | null;
  medien_limit_mb: number | null;
  created_at: string | null;
  expires_at: string | null;
  used_at: string | null;
  zurueckgezogen_at: string | null;
  user_id: number | null;
  eingeloest_von: string | null;
  eingeloest_von_name: string | null;
  zustand: Einladungszustand;
}

/**
 * Die Antwort auf `POST /admin/einladungen`.
 *
 * `link` steht **nur hier** und in keiner Liste: In der Datenbank liegt der
 * sha256-Hash, der Server kann die Adresse nicht wiederherstellen. Wer sie
 * verliert, nimmt die Einladung zurück und legt eine neue an.
 */
export interface FrischeEinladung {
  id: number;
  link: string;
  tage: number;
  funktionen_lesbar: string[];
  hinweis: string;
}

/** Eine Zeile der Kontingent-Tabelle: ein Konto, eine Art, die Summe über 30 Tage. */
export interface Nutzungszeile {
  user_id: number | null;
  email: string | null;
  display_name: string | null;
  /** Welches fremde Kontingent gezogen wurde — `routing`, `geocoding`, … */
  art: string;
  anzahl: number;
  /** Der letzte Tag, an dem etwas gezählt wurde. Trennt Ausreißer von Dauerzustand. */
  zuletzt: string | null;
}

export interface AdminStatistik {
  konten: number;
  konten_aktiv: number;
  konten_admin: number;
  reisen: number;
  reisen_papierkorb: number;
  tagebucheintraege: number;
  medien: number;
  medien_gb: number;
  einladungen_offen: number;
  /**
   * Der Platz auf der Platte — oder `null`.
   *
   * Nicht dieselbe Zahl wie `medien_gb`: Auf derselben Partition liegen auch
   * Datenbank, Logs und Vorschaubilder. Voll ist voll, egal was die
   * Kontingente sagen. `null`, wenn der Medienordner nicht lesbar ist — dann
   * fehlt die Angabe, und sie fehlt sichtbar.
   */
  platte: { pfad: string; gesamt_gb: number; belegt_gb: number; frei_gb: number } | null;
  nutzung_30_tage: Nutzungszeile[];
}

export class ApiError extends Error {
  /**
   * Das rohe `detail` der Antwort — meistens derselbe Satz wie `message`.
   *
   * Es steht daneben, weil der Server dort seit dem Reisebuch auch ein
   * **Objekt** ablegen kann: Beim Versuch, ein zweites Buch zu setzen, trägt
   * der 409 den Beizettel des liegenden (Vorlage, Seiten, Zeitpunkt). In
   * `message` gepresst wäre das ein `[object Object]`, und die einzige
   * brauchbare Auskunft der ganzen Antwort wäre verloren. Wer es auswertet,
   * tut das in einer reinen Funktion — `reisebuch.nachAblehnung`.
   */
  constructor(
    readonly status: number,
    message: string,
    readonly detail: unknown = null
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(`/api${path}`, { ...init, headers });

  // 401 heißt: Session abgelaufen oder widerrufen. Lokal wegräumen, sonst
  // versucht die App bei jedem Aufruf weiter mit einem toten Token.
  if (res.status === 401) {
    clearToken();
    throw new ApiError(401, 'Nicht angemeldet.');
  }
  if (!res.ok) {
    throw await fehlerAus(res);
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

/**
 * Wie `request`, gibt aber die Kopfzeilen mit zurück.
 *
 * Gebraucht für `ETag`: Nach dem Schreiben der Stationen steht dort die neue
 * Revision. Sie von dort zu nehmen erspart ein zweites Laden der Reise — und
 * vor allem das Zeitfenster dazwischen, in dem schon wieder jemand geschrieben
 * haben könnte.
 */
async function requestMitKopf<T>(
  path: string,
  init: RequestInit = {}
): Promise<{ daten: T; etag: number | null }> {
  const token = getToken();
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(`/api${path}`, { ...init, headers });
  if (res.status === 401) {
    clearToken();
    throw new ApiError(401, 'Nicht angemeldet.');
  }
  if (!res.ok) throw await fehlerAus(res);

  const roh = res.headers.get('ETag');
  const etag = roh === null ? null : Number(roh.replace(/"/g, ''));
  return {
    daten: (res.status === 204 ? undefined : await res.json()) as T,
    etag: Number.isFinite(etag) ? etag : null
  };
}

/**
 * Der Fehler zu einer abgelehnten Antwort — Text und rohes `detail`.
 *
 * Beides in einem Griff, weil der Antwortkörper nur **einmal** gelesen werden
 * kann: Ein zweiter Aufruf bekäme einen verbrauchten Stream. Der Text ist der
 * Satz des Servers, wo er einen schickt; steht dort ein Objekt oder eine Liste
 * (FastAPI meldet seine eigenen Prüffehler so), bleibt der Statuszeile das
 * letzte Wort, und die Auswertung des Objekts ist Sache des Aufrufers.
 */
async function fehlerAus(res: Response): Promise<ApiError> {
  let detail: unknown = null;
  try {
    detail = (await res.json())?.detail ?? null;
  } catch {
    detail = null;
  }
  const text = typeof detail === 'string' && detail.trim()
    ? detail
    : `${res.status} ${res.statusText}`;
  return new ApiError(res.status, text, detail);
}

/** Vom Nutzer gesetzte Station. Reihenfolge im Array = Reihenfolge der Reise. */
/** Was das Auto über sich sagt. Alles lesend — nichts hiervon schaltet etwas. */
export interface AutoZustand {
  vin: string;
  /** Ladestand in Prozent. */
  soc: number | null;
  reichweite_km: number | null;
  km_stand: number | null;
  lat: number | null;
  lon: number | null;
  /** Traut das Auto seinem eigenen Standort? In der Tiefgarage nicht. */
  ort_verlaesslich: boolean;
  laedt: boolean;
  ladeleistung_kw: number | null;
  ziel_soc: number | null;
  /** Zeitstempel des Fahrzeugs selbst (ISO). */
  stand: string | null;
  /**
   * Wie alt die **Messung des Autos** ist, in Sekunden.
   *
   * Das ist die Zahl, die in die Anzeige gehört — nicht `alter_s`. Ein
   * stehendes Auto meldet sich stundenlang nicht; beim ersten echten Abruf war
   * unsere Antwort null Sekunden alt und die Messung vierunddreißig Stunden.
   */
  stand_alter_s: number | null;
  /** Wie alt **unsere** Antwort ist (Zwischenspeicher des Servers). */
  alter_s: number;
  aus_speicher: boolean;
  /** Die Cloud war nicht erreichbar — das hier ist der letzte bekannte Stand. */
  veraltet?: boolean;
}

import type { Stufen, Verkehrsmittel } from './planner/abstecher';
import type { AusflugLeg } from './planner/scheduler';
// Eigene Zeile und nicht mit der darüber zusammengezogen: Der Bestandstest
// `ausflug: der Typ der Strecke kommt aus dem Rechenkern` prüft jene Zeile
// wortgleich — und die Zusage, die er absichert (der Typ hat genau eine
// Fassung, und die steht im Kern), gilt für dieses Feld genauso.
import type { Tagesprofil } from './planner/scheduler';
// Und aus demselben Grund eine dritte Zeile: Die Form der Höhenmeter steht im
// Kern (`SchedLeg.elevation`), weil dort damit gerechnet wird.
import type { Hoehenprofil } from './planner/scheduler';
// Die Form des Plan-Schnappschusses steht im Kern, weil ihn dort alle drei
// Läufer bauen (Vertrag § 2). **Nur der Typ**, und das ist keine Kosmetik:
// `api.ts` hängt an jeder Fläche dieser Anwendung, auch an der Anmeldung. Ein
// Wert-Import zöge Scheduler, Achse und Ladeplaner in das erste Bündel, das
// der Browser holt — rund achtzig Kilobyte, die nur der Planer braucht, und
// der wird ohnehin nachgeladen.
import type { Schnappschuss } from './planner/eingabe';
import type { Quartier } from './planner/quartiere';
import type { Tagesplan } from './planner/tagesplan';
import type {
  EinhaengungMitFussweg,
  Fusswegantwort,
  GespeicherterFussweg,
  Ortspaar
} from './fussweg';
import type { Kostenstand } from './kosten';
import type { Tagebucheintrag } from './tagebuch';
import { anhangWeg, ohneAnhaenge, type Anhang } from './anhaenge';

/* ══ Verlässlichkeit vor Ort — Ortsschärfe, Lage, Ausweichziel ═══════
 *
 * Drei Formen, die der Server seit dem 5.9.2026 führt (`ortsschaerfe.py`,
 * `lage.py`). Sie stehen hier und nicht in einem eigenen Modul, weil sie
 * **Serverformen** sind wie `Quartier` oder `Anhang` — was daraus abgeleitet
 * wird (Ton, Text, Zählung), steht in `lib/verlaesslichkeit.ts` und ist ohne
 * Netz prüfbar.
 */

/**
 * Was an einer Station unscharf ist — genau eine der vier Prüfungen.
 *
 * `strasse` — der Geocoder-Treffer hinter der Adresse war eine Straße oder
 * eine Fläche, ihr Punkt also ein Mittelwert (±500 m). `adresse_fern` — die
 * gespeicherte Adresse vorwärts geocodiert liegt über 150 m von der
 * gespeicherten Koordinate. `parkhaus_daneben` — es gibt ein OSM-Parkobjekt
 * in Reichweite, die Station steht aber nicht darauf. `parkhaus_fehlt` — die
 * Station hat Park-Bezug, in 300 m steht kein Parkobjekt. `unklar` — die
 * Prüfung selbst ist gescheitert (Overpass down, Geocoder stumm); sie ist
 * ausdrücklich keine Entwarnung.
 */
export type Befundart =
  | 'strasse'
  | 'adresse_fern'
  | 'parkhaus_daneben'
  | 'parkhaus_fehlt'
  | 'unklar';

/** Der Punkt, auf den ein `parkhaus_daneben`-Befund einrasten will. */
export interface Befundvorschlag {
  lat: number;
  lon: number;
  /** Der OSM-Name, wenn das Objekt einen trägt — sonst fehlt er. */
  name?: string | null;
  adresse?: string | null;
  /** `multi-storey`, `underground`, `surface` … — die OSM-Bauart. */
  bauart?: string | null;
  /** Die OSM-Kennung (`node/123`), damit der Punkt nachschlagbar bleibt. */
  osm?: string | null;
}

/** Ein einzelner Befund an der Station. */
export interface Ortsschaerfebefund {
  art: Befundart;
  /**
   * Das Wort zur Art — es kommt **vom Server** (`ortsschaerfe.ARTEN_TEXT`).
   *
   * Optional, weil die Spalte es nicht speichert: An der Station steht nur
   * `art`, das Wort setzt erst die Antwort des Prüflaufs dazu. Die Oberfläche
   * darf sich deshalb nicht darauf verlassen und hat ihre eigene Zuordnung
   * (`verlaesslichkeit.BEFUND_TITEL`) — dieselbe Wahrheit, an der Stelle, an
   * der sie ohne Netz verfügbar sein muss.
   */
  art_text?: string | null;
  hinweis?: string | null;
  distanz_m?: number | null;
  vorschlag?: Befundvorschlag | null;
  /** Die Station wurde bereits auf den Vorschlag gesetzt. */
  eingerastet?: boolean;
}

/**
 * Der Ortsschärfe-Block an der Station — `stops.ortsschaerfe`.
 *
 * **Muss beim Speichern mitgeschickt werden**, aus demselben Grund wie `warum`
 * und `tagesprofil`: `PUT /stops` ersetzt die Liste vollständig, und ein Feld,
 * das der Typ nicht kennt, schickt dieser Client auch nicht mit. Ein
 * Prüfergebnis überlebte jede Web-Speicherung dann nur noch über die
 * Feldrettung in `db.replace_stops`.
 *
 * `bestaetigt` ist die Aussage eines Menschen („Lage stimmt so") und gilt für
 * **diese Koordinate**: Wandert die Station, fällt sie zurück auf `false` —
 * serverseitig in `ortsschaerfe.bestaetigung_nachziehen`, in der Oberfläche
 * beim Marker-Ziehen sofort, damit die Zusage nicht bis zum nächsten Laden
 * stumm falsch dasteht.
 */
export interface Ortsschaerfe {
  befunde: Ortsschaerfebefund[];
  /** Wann geprüft wurde (`JJJJ-MM-TT HH:MM:SS` Hauszeit). `null` heißt: nie. */
  geprueft_am?: string | null;
  bestaetigt?: boolean;
}

/**
 * Plan B für den Tag — `stops.ausweichziel` (V4).
 *
 * Erlaubt an Ketten- und Ausflugsstationen, nicht am Abstecher; die Regel
 * steht serverseitig in `ortsschaerfe.ausweichziel_lesen` und in der
 * Sichtbarkeitsweiche des Werkbank-Abschnitts. `name` ist das einzige
 * Pflichtstück — ein Ausweichziel ohne Namen wäre keines. Koordinaten sind
 * Kür: „Desenzano-Altstadt" hilft auch ohne Zahlenpaar.
 *
 * **Muss beim Speichern mitgeschickt werden** — derselbe Grund wie oben.
 */
export interface Ausweichziel {
  name: string;
  hinweis?: string | null;
  lat?: number | null;
  lon?: number | null;
}

/** Eine Station mit offenen Befunden, wie der Prüflauf sie aufzählt. */
export interface Ortsschaerfezeile {
  /** Die Stelle in der gespeicherten Liste, 0-basiert — der Sprungpunkt. */
  position: number;
  name?: string | null;
  lat?: number | null;
  lon?: number | null;
  adresse?: string | null;
  befunde: Ortsschaerfebefund[];
}

/** Eine Station, die der Prüflauf auf den OSM-Parkplatz gesetzt hat. */
export interface Eingerastet {
  position: number;
  name?: string | null;
  lat: number;
  lon: number;
  verschoben_m?: number | null;
  adresse_geleert?: boolean;
}

/**
 * Die Antwort von `POST /trips/{id}/ortsschaerfe`.
 *
 * `eingerastet` und `rat` stehen nur da, wenn wirklich etwas bewegt wurde —
 * und das ist der Teil, der die Oberfläche zum Handeln zwingt: Eine
 * verschobene Station verschiebt ihre Etappe, also muss die Route neu.
 */
export interface Ortsschaerfeergebnis {
  geprueft_am?: string | null;
  stationen: number;
  auffaellig: number;
  befunde: Ortsschaerfezeile[];
  hinweis?: string | null;
  eingerastet?: Eingerastet[];
  rat?: string | null;
}

/** Wie es an einem Ort an einem Tag aussieht. `unklar` ist keine Entwarnung. */
export type Lagestatus = 'frei' | 'eingeschraenkt' | 'gesperrt' | 'unklar';

/** Woran es liegt — `lage.ART_TEXT` im Backend. */
export type Lageart =
  | 'veranstaltung'
  | 'streik'
  | 'sperrung'
  | 'fahrverbot'
  | 'faehre'
  | 'sonstiges';

/** Ein Lage-Befund (Ort × Kalendertag), wie `POST /trips/{id}/lage` ihn ausgibt. */
export interface Lagebefund {
  ort?: string | null;
  /** `JJJJ-MM-TT`. */
  datum?: string | null;
  status: Lagestatus;
  /** Das Wort zum Status, vom Server (`lage.STATUS_TEXT`). */
  status_text?: string | null;
  art?: Lageart | string | null;
  art_text?: string | null;
  titel?: string | null;
  einzelheiten?: string | null;
  zeitraum?: string | null;
  /**
   * Worauf die Auskunft beruht. Geht immer mit, auch als `null` — ein Status
   * ohne Quelle ist eine Behauptung, und genau das soll man ihm ansehen.
   */
  quelle_url?: string | null;
  stand?: string | null;
  /** Die fertige Zeile (`lage.zeile`) — sie wird gezeigt, nicht neu gebaut. */
  anzeige?: string | null;
}

/**
 * Ein recherchierter Pass, wie er in `trip.settings.paesse.eintraege` steht.
 *
 * Gelesen wird er im Web ausschließlich aus den Einstellungen und nicht aus
 * der Antwort des Prüflaufs: Der Reiter zeigt den **Stand**, und der überlebt
 * das Neuladen. Die Antwort ist nur die Quittung des Laufs.
 *
 * Alle Felder optional — ein einzelner kaputter Eintrag darf die Liste nicht
 * sprengen; der Server liest den Block aus demselben Grund tolerant
 * (`paesse.gelesen`).
 */
export interface Passeintrag {
  name?: string | null;
  hoehe_m?: number | null;
  /** `frei`, `gesperrt`, `eingeschraenkt`, `unklar` — dieselben Worte wie bei der Lage. */
  status?: string | null;
  einzelheiten?: string | null;
  zeitraum?: string | null;
  quelle_url?: string | null;
  stand?: string | null;
  etappe_von?: string | null;
  etappe_nach?: string | null;
  /** Die Stelle der Zielstation in der Liste — der Sprungpunkt. */
  etappe_nach_position?: number | null;
  lat?: number | null;
  lon?: number | null;
}

/** `trip.settings.paesse` — `undefined` heißt: nie geprüft. */
export interface Passblock {
  geprueft_am?: string | null;
  eintraege?: Passeintrag[] | null;
  etappen_ohne_profil?: unknown[] | null;
  nicht_geprueft?: unknown[] | null;
}

/** Die Antwort von `POST /trips/{id}/paesse`. */
export interface Passergebnis {
  geprueft_am?: string | null;
  gefunden: number;
  warnungen: number;
  paesse?: Passeintrag[] | null;
  hinweis?: string | null;
  etappen_ohne_profil?: unknown[] | null;
  nicht_geprueft?: unknown[] | null;
  luecke?: string | null;
  gekappt?: string | null;
}

/** `trip.settings.lage` — der Planungsstand vor der Abfahrt. */
export interface Lageblock {
  gerechnet_am?: string | null;
  befunde?: Lagebefund[] | null;
  /** Nur im Tageszwischenspeicher `lage_morgen` gesetzt. */
  datum?: string | null;
  nicht_geprueft?: unknown[] | null;
}

/** Die Antwort von `POST /trips/{id}/lage`. */
export interface Lageergebnis {
  gerechnet_am?: string | null;
  gefragt: number;
  warnungen: number;
  befunde: Lagebefund[];
  hinweis?: string | null;
  /** `lage` (ganze Reise) oder `lage_morgen` (ein Tag) — wohin geschrieben wurde. */
  gespeichert_als?: 'lage' | 'lage_morgen' | string;
  datum?: string | null;
  /** Was der Deckel abgeschnitten hat — offen und nicht unauffällig. */
  nicht_geprueft?: unknown[] | null;
  gekappt?: string | null;
}

/* ══ Läufe im Hintergrund ═════════════════════════════════════════════
 *
 * Seit dem 6.9.2026 antwortet `POST /trips/{id}/lage` nicht mehr mit dem
 * Ergebnis, sondern mit **202 und einer Quittung**: Der Lauf ist angestoßen,
 * die Antwort kommt später. Der Grund ist kein Geschmack — nginx kappt nach
 * 60 Sekunden, und die Herbstreise hat bis zu 40 Ort-Tag-Paare, von denen
 * jedes eine Websuche kostet. Der synchrone Weg lieferte dort verlässlich
 * einen 504 über einem Lauf, der im Server weiterlief.
 *
 * Gelesen wird der Fortschritt danach über `GET …/lage/stand`. Die Befunde
 * erscheinen dort **fortschreitend**, schon während der Lauf noch läuft, und
 * landen zugleich in `trip.settings.lage` — der Stand überlebt also ein
 * Neuladen, auch wenn niemand mehr zusieht.
 */

/**
 * Was jeder Hintergrundlauf über sich sagt — Lage wie Ortsschärfe.
 *
 * `laeuft` ist das einzige Pflichtfeld und die einzige Autorität: Ein Schritt
 * gilt genau so lange als offen, wie hier `true` steht. `fertig_am` und
 * `fehler` sind Auskünfte **über** das Ende, nicht sein Beweis — ein Lauf, der
 * einen Teilfehler meldet und weiterläuft, wäre sonst vorzeitig abgeschrieben.
 */
export interface Laufstand {
  laeuft: boolean;
  /** Wie viele Einheiten der Lauf sich vorgenommen hat (Ort-Tag-Paare bzw. Stationen). */
  gefragt?: number | null;
  /** Wie viele davon schon durch sind. */
  geprueft?: number | null;
  /** Wann er fertig wurde — ISO. Fehlt, solange er läuft. */
  fertig_am?: string | null;
  /** Woran er gescheitert ist, als lesbarer deutscher Satz. */
  fehler?: string | null;
}

/**
 * Die 202-Quittung von `POST /trips/{id}/lage`.
 *
 * Ein zweiter Aufruf während eines Laufs liefert **denselben** Stand und
 * startet keinen zweiten — die Fläche darf den Knopf also drücken, ohne vorher
 * zu fragen.
 */
export interface Lagelauf extends Laufstand {
  /** `lage` (ganze Reise) oder `lage_morgen` (ein Tag) — wohin geschrieben wird. */
  gespeichert_als?: 'lage' | 'lage_morgen' | string;
}

/**
 * `GET /trips/{id}/lage/stand` — der Fortschritt und was bisher gefunden wurde.
 *
 * `befunde` wächst während des Laufs. Zusammengeführt wird in der Fläche über
 * `verlaesslichkeit.befundeZusammen`, damit ein Befund, der in zwei Ständen
 * hintereinander steht, nicht zweimal in der Liste landet.
 */
export interface Lagestand extends Laufstand {
  warnungen: number;
  befunde: Lagebefund[];
  gespeichert_als?: 'lage' | 'lage_morgen' | string;
  datum?: string | null;
}

/**
 * Die Antwort auf `POST /trips/{id}/lage` — Quittung **oder** Ergebnis.
 *
 * Beides, weil der Server, gegen den dieser Client läuft, älter sein darf als
 * der Vertrag: Ein Stand ohne Hintergrundlauf antwortet weiter synchron mit
 * `Lageergebnis`, und dann funktioniert alles wie vorher. Unterschieden wird
 * über `laeuft` (`verlaesslichkeit.laeuftImHintergrund`) und nicht über den
 * Statuscode — der steht in `request` nicht mehr zur Verfügung.
 */
export type Lageantwort = Lageergebnis | Lagelauf;

/**
 * `GET /trips/{id}/ortsschaerfe/stand` — dieselbe Form, andere Einheit.
 *
 * Steht hier auf Verdacht: Der Ortsschärfe-POST antwortet heute noch synchron.
 * Tut er es eines Tages nicht mehr, ist die Fläche schon darauf eingerichtet —
 * sie unterscheidet die beiden Fälle an `laeuft` und sonst nirgends.
 */
export interface Ortsschaerfestand extends Laufstand {
  auffaellig: number;
  befunde: Ortsschaerfezeile[];
  eingerastet?: Eingerastet[];
}

/** Die Antwort auf `POST /trips/{id}/ortsschaerfe` — Quittung **oder** Ergebnis. */
export type Ortsschaerfeantwort = Ortsschaerfeergebnis | Laufstand;

/* ══ Befunde — eine Form für alles vor der Abfahrt ════════════════════
 *
 * Seit dem 12.9.2026 hat der Server **eine** Leseform für alles, was vor der
 * Abfahrt noch etwas will: Ortsschärfe, Lage, Pässe, ZTL, Vignetten,
 * Buchungen, Ladelücken, Plansummen, Wochentag, Dunkelheit. Vorher waren das
 * sieben Formen mit sieben Schweren, und jede Fläche erfand ihre eigene
 * Zählung dazu — der Vorbereitung-Reiter zählte im Kopf **Abschnitte**, weil
 * über sieben Formen keine Summe bildbar war.
 *
 * Diese Typen sind deshalb nachgiebig geschrieben (fast alles optional): Der
 * Browser darf älter sein als der Server und umgekehrt. Was der Browser
 * daraus liest, steht in `lib/befunde.ts` — hier stehen nur die Formen.
 */

/** Die eine Schwere-Skala. Die Reihenfolge **ist** die Sortierung. */
export type Befundschwere = 'meiden' | 'handeln' | 'pruefen' | 'hinweis';

/**
 * Die Befundarten aus § 2 des Vertrags — `befunde.ARTEN` im Backend.
 *
 * `Befundsorte` und nicht `Befundart`: Der Name war schon vergeben, und zwar
 * von den fünf Arten eines **Ortsschärfe**-Befundes (oben). Zwei Typen
 * gleichen Namens in einer Datei gehen nicht, und den älteren umzubenennen
 * hieße, vier fremde Dateien anzufassen, die nichts mit den Befunden zu tun
 * haben.
 */
export type Befundsorte =
  | 'ortsschaerfe'
  | 'lage'
  | 'pass'
  | 'ztl'
  | 'vignette'
  | 'buchung'
  | 'laden'
  | 'plan'
  | 'wochentag'
  | 'sonnenuntergang'
  // Seit dem 14.9.2026: die Panoramastraße auf der Etappe und das Land,
  // in dem man ab heute unterwegs ist. Beide sind Hinweise ohne
  // Handlungsbedarf und werden von der Liste generisch gezeigt.
  | 'panoramastrasse'
  | 'land';

/**
 * Worauf sich ein Befund bezieht.
 *
 * `station_id` steht mit dabei, `position` ist aber der Sprungpunkt: Die
 * Kennung einer Station überlebt kein `PUT /stops`, die Stelle in der Liste
 * ist die Adresse, unter der der Server sie kennt (`ord`).
 */
export interface Befundbezug {
  typ?: 'station' | 'tag' | 'reise' | 'fahrzeug' | string;
  station_id?: number | null;
  position?: number | null;
  name?: string | null;
  /** `JJJJ-MM-TT`, wenn ein Tag bekannt ist — sonst ausdrücklich `null`. */
  datum?: string | null;
  /** Bei `typ: reise` steht hier das Land (Vignetten, Mautzugang). */
  land?: string | null;
}

/**
 * Was aus dem Befund folgt — ein Sprung oder ein Knopf.
 *
 * `keine` gibt es auch: Ein Wochentag-Hinweis will nichts von einem. Die
 * Unterscheidung steht im Feld und nicht in einer Namensliste in der Fläche,
 * damit ein neues Ziel dort nicht als Knopf ohne Wirkung landet.
 */
export interface Befundhandlung {
  art?: 'sprung' | 'knopf' | 'keine' | string;
  /**
   * Wohin: `station`, `plan`, `laden`, `fahrzeug_ztl` (Sprung) bzw.
   * `einrasten`, `bestaetigen`, `vignette_hinterlegen`,
   * `mautzugang_hinterlegen` (Knopf).
   */
  ziel?: string | null;
  station_id?: number | null;
  position?: number | null;
  /** Die Aufschrift. Sie kommt vom Server — zwei Formulierungen wären zwei Knöpfe. */
  text?: string | null;
  /** Bei den Maut-Knöpfen: das Land und die Vorbelegung des Dialogs. */
  land?: string | null;
  vorbelegung?: unknown;
  /** Bei der Lage: der Plan B, den der Server an der Station gefunden hat. */
  ausweichziel?: string | null;
}

/** Woher die Auskunft kommt. Ein Status ohne Quelle ist eine Behauptung. */
export interface Befundquelle {
  name?: string | null;
  url?: string | null;
  /** „laut Abruf vom 30.8." — die zweite Hälfte jeder recherchierten Auskunft. */
  stand?: string | null;
}

/** Ein Befund in der Form aus § 1 des Vertrags. */
export interface Befund {
  /** `sha1(art, bezug, schluessel)[:12]` — stabil, solange die Quelle gleich bleibt. */
  id: string;
  art: Befundsorte | string;
  bezug?: Befundbezug | null;
  schwere: Befundschwere | string;
  /** Der ganze Satz — er wird gezeigt, nicht neu gebaut. */
  satz: string;
  /** Der Halbsatz für Aufzählungen und Listenzeilen. */
  kurz?: string | null;
  handlung?: Befundhandlung | null;
  quelle?: Befundquelle | null;
  geprueft_am?: string | null;
  gilt_bis?: string | null;
  /** Ob er auf die geteilte Seite darf. In v1 nur Wochentag und Dunkelheit. */
  oeffentlich?: boolean | null;
  /** `null` oder `ausgeblendet` — aus der Tabelle `befund_zustand`. */
  zustand?: 'ausgeblendet' | string | null;
  notiz?: string | null;
  ausgeblendet_am?: string | null;
}

/**
 * `{meiden, handeln, pruefen, hinweis, offen}` — überall dieselbe Rechnung.
 *
 * Die vier Stufen zählen **alles**, auch Ausgeblendetes; `offen` ist der
 * Handlungsbedarf und zählt es nicht. Das ist die Zahl im Panelkopf.
 */
export interface Befundzaehlung {
  meiden?: number;
  handeln?: number;
  pruefen?: number;
  hinweis?: number;
  offen?: number;
}

/** Wann zuletzt nachgesehen wurde — `null` heißt „nie", nicht „nichts gefunden". */
export interface Befundgeprueft {
  ortsschaerfe_am?: string | null;
  lage_am?: string | null;
  paesse_am?: string | null;
}

/**
 * `GET /trips/{id}/befunde` — die ganze Sicht, ohne Seiteneffekt.
 *
 * Sie kostet nichts und darf nach jeder Änderung geholt werden: Gelesen
 * werden Stationen, `trip.settings`, das Fahrzeug, der Plan-Schnappschuss und
 * die Ausblenden-Tabelle. Wer **prüfen** will, ruft `POST …/pruefen`.
 *
 * Ein 404 heißt nicht „keine Befunde", sondern „dieser Server kennt sie noch
 * nicht" — die Fläche fällt dafür auf den Stand von vorher zurück
 * (`befunde.istRueckfall`).
 */
/* ══ Länder unterwegs ════════════════════════════════════════════════════
 *
 * Die Formen aus § 3 des Vertrags Chicago → Montreal. Sie stehen hier als
 * Typen und nirgends als Inhalt: Was in einem Land gilt, entscheidet der
 * Katalog am Server (`laender.py`) und nicht der Browser — eine zweite
 * Sammlung Landeswissen wäre eine zweite Wahrheit, und die veraltet.
 *
 * **Alle Felder optional.** Ein Server, der ein Feld noch nicht liefert, darf
 * keinen Typfehler auslösen, und eine Gruppe ohne Sätze fällt in der Fläche
 * weg (dieselbe Regel wie bei den Befunden). Sichtbar wird daraus eine leere
 * Karte und keine kaputte.
 */

/** Die Währung eines Landes — Code, Zeichen und Name. */
export interface Landwaehrung {
  code?: string | null;
  zeichen?: string | null;
  name?: string | null;
}

/** Notruf: die Nummer und der Satz dazu. */
export interface Landnotruf {
  allgemein?: string | null;
  hinweis?: string | null;
}

/** Landesvorwahl und der Satz zum Roaming. */
export interface Landtelefon {
  vorwahl?: string | null;
  hinweis?: string | null;
}

/** Maße und Gewichte, wie sie dort angeschrieben stehen. */
export interface Landeinheiten {
  entfernung?: string | null;
  geschwindigkeit?: string | null;
  temperatur?: string | null;
  kraftstoff?: string | null;
}

/**
 * Ein Feiertag eines Landes — `datum` als `MM-TT`, ohne Jahr.
 *
 * Ohne Jahr, weil der Katalog ihn so führt: Die meisten dieser Tage liegen
 * jedes Jahr auf demselben Datum, und ein Jahr an dieser Stelle wäre eine
 * Genauigkeit, die die Angabe nicht hat. Welche davon in den Reisezeitraum
 * fallen, rechnet `feiertage()` in `laender.ts` aus.
 */
export interface Landfeiertag {
  /** `MM-TT`, zum Beispiel `09-30`. */
  datum: string;
  name: string;
}

/**
 * Ein Land der Reise.
 *
 * Die Satzgruppen (`dos`, `donts`, `verkehr`, `unterwegs`, `zahlen`,
 * `essen_trinken`, `umgang`, `sprache`, `sicherheit_gesundheit`, `praktisch`
 * und der Hinweis am Telefon) sind Sätze für Menschen und werden **gezeigt,
 * nicht gebaut**: Sie kommen so aus dem Katalog, samt Spannen und samt „vor
 * Ort erfragen" dort, wo es keine belastbare Regel gibt.
 */
export interface Landeintrag {
  code: string;
  name?: string | null;
  sprachen?: string | null;
  waehrung?: Landwaehrung | null;
  notruf?: Landnotruf | null;
  telefon?: Landtelefon | null;
  einheiten?: Landeinheiten | null;
  verkehr?: string[] | null;
  zahlen?: string[] | null;
  umgang?: string[] | null;
  praktisch?: string[] | null;
  /* ── Die sechs Listen aus dem zweiten Teil (15.9.2026) ──────────────
   *
   * Vertrag `Plans/VERTRAG-LAENDER-2-2026-09-15.md`, § 1. Alle optional wie
   * die vier darüber: Ein Server, der sie noch nicht ausliefert, ist kein
   * Fehler — die Gruppe fällt dann aus der Karte (siehe `landgruppen`). */
  /** Was man tun sollte — der erste Block der Karte, offen. */
  dos?: string[] | null;
  /** Was man lassen sollte — der zweite Block, offen. */
  donts?: string[] | null;
  /** Wörter und Sätze, die weiterhelfen, je als „Wort — Bedeutung". */
  sprache?: string[] | null;
  /** Essenszeiten, Bestellen, Trinkwasser, typische Gerichte. */
  essen_trinken?: string[] | null;
  /** Apotheke, Arzt, Versicherung, Kriminalität mit Augenmaß, Wetter. */
  sicherheit_gesundheit?: string[] | null;
  /** Parken, Tanken und Laden, Toiletten, Polizei, Unfall, Abschleppen. */
  unterwegs?: string[] | null;
  /** Die Zeitzonen als **Satz** („Vier Zonen im Kernland: …"), nicht als
   * Kennung: Die Zone, mit der gerechnet wird, kommt aus den Polygonen. */
  zeitzone?: string | null;
  /** Die bundesweiten Feiertage des Jahres — `datum` ohne Jahr. */
  feiertage?: Landfeiertag[] | null;
  /** Der Vorbehalt des Katalogs — „Richtwerte Stand 2026, …". */
  hinweis?: string | null;
  /** Der Satz für den Grenztag: „Ab heute Kanada: km/h statt mph, …". */
  grenzsatz?: string | null;
  stand?: string | null;
  /** Sind die Angaben Richtwerte? Im Katalog immer `true`. */
  ca?: boolean | null;
  /** Beginnt die Reise in diesem Land? Dann gibt es keinen Grenztag. */
  startland?: boolean | null;
  /** `JJJJ-MM-TT` — der Tag, an dem die Reise dieses Land erreicht. */
  grenztag?: string | null;
}

/**
 * `GET /trips/{id}/laender` — die Länder dieser Reise, in Reisefolge.
 *
 * Ein **404** heißt „dieser Server kennt die Länder noch nicht" und ist kein
 * Fehlerzustand: Die Karte bleibt dann leer und sagt es (dieselbe
 * Unterscheidung wie beim Mautstand und bei den Befunden).
 */
export interface Landsicht {
  laender: Landeintrag[];
  stand?: string | null;
}

/** `GET /laender` — der ganze Katalog, ohne Reise. Für die Währungsvorgabe. */
export interface Landkatalog {
  laender: Landeintrag[];
  stand?: string | null;
}

export interface Befundsicht {
  befunde: Befund[];
  zaehlung?: Befundzaehlung | null;
  /** Dieselbe Zählung je Art — daraus nehmen die Abschnitte Zahl und Ton. */
  je_art?: Record<string, Befundzaehlung> | null;
  geprueft?: Befundgeprueft | null;
  /** Der Stand des Plan-Schnappschusses, aus dem `laden` und `plan` kommen. */
  stand?: string | null;
}

/** Die Antwort der beiden Ausblenden-Wege — mit der frischen Zählung. */
export interface Befundzustand {
  befund_id: string;
  zustand?: 'ausgeblendet' | null;
  notiz?: string | null;
  war_ausgeblendet?: boolean;
  zaehlung?: Befundzaehlung | null;
}

/**
 * Ein Schritt des Sammellaufs, wie der Server ihn nennt.
 *
 * Zwei Gesichter, je nachdem, wer antwortet: Die **Quittung** des POST nennt
 * die geplanten Schritte mit ihrem Satz (`was`), der **Stand** trägt danach je
 * gelaufenem Schritt sein Ergebnis — `geprueft` (eine Zahl), `uebersprungen`
 * (mit Grund) oder `fehler`. Alles optional, weil beide Formen dieselbe Liste
 * füllen und ein Client nicht raten soll, welche er vor sich hat.
 *
 * `uebersprungen` und `fehler` sind die wichtigen: Ohne sie sähe eine leere
 * Befundliste nach einer Entwarnung aus, obwohl ein Schritt gar nicht gelaufen
 * ist (kein gespeicherter Plan, keine gerechnete Route, kein Schlüssel).
 */
export interface Pruefschritt {
  schritt: string;
  was?: string | null;
  geprueft?: number | null;
  uebersprungen?: string | null;
  fehler?: string | null;
  nicht_geprueft?: unknown[] | null;
}

/** Der Feinfortschritt innerhalb eines Schrittes — „7 von 40". */
export interface Pruefteilstand {
  schritt?: string | null;
  geprueft?: number | null;
  gefragt?: number | null;
}

/**
 * `POST /trips/{id}/pruefen` (202) und `GET …/pruefen/stand`.
 *
 * `von`/`bis` zählen die **Schritte** (Ortsschärfe → Lage → Pässe), der
 * `teilstand` zählt innerhalb eines Schrittes. Beide braucht die Fläche: Eine
 * Reise mit 168 Stationen stünde sonst sieben Minuten auf einem Drittel.
 *
 * Die Reihenfolge kommt seit dem 12.9.2026 vom Server. Sie stand vorher
 * doppelt — hier und dort —, und die Ortsschärfe muss zuerst laufen, weil sie
 * Stationen bewegt.
 */
export interface Pruefstand extends Laufstand {
  schritt?: string | null;
  von?: number | null;
  bis?: number | null;
  schritte?: Pruefschritt[] | null;
  teilstand?: Pruefteilstand | null;
  /** Was das Einrasten getroffen hat — der Ortsschärfe-Schritt legt es ab. */
  eingerastet?: unknown[] | null;
  hinweis?: string | null;
  rat?: string | null;
}

export interface Stop {
  id?: number;
  ord?: number;
  lat: number;
  lon: number;
  name: string | null;
  address: string | null;
  /**
   * Die Art des Halts — dieselbe Liste wie `_ENUM_WERTE` im Server.
   *
   * `ausflug` steht hinten und ist die jüngste: eine Fahrt, die zurückkommt.
   * Das Auto fährt sie an (anders als den Abstecher), die Kette geht trotzdem
   * am Träger weiter — die drei Prädikate dazu stehen in
   * `planner/abstecher.ts`, und welches wo gilt, ist dort einzeln begründet.
   */
  kind: 'visit' | 'sleep' | 'charge' | 'passthru' | 'paddle' | 'ferry' | 'abstecher' | 'ausflug';
  duration_ms: number;
  nights: number;
  notes: string | null;
  skip: boolean;
  /**
   * Warum dieser Ort — ein bis zwei Sätze, höchstens 280 Zeichen
   * (`WARUM_MAX` in `lib/briefing.ts`, wortgleich mit der Spalte).
   *
   * Die zweite Prosa-Angabe der Station neben `notes`, und sie beantwortet
   * eine andere Frage: Die Notiz ist für **mich unterwegs** („Rezeption bis 20
   * Uhr, Code 4711"), der Warum-Satz ist der Grund, aus dem die Station im
   * Plan steht. Er ist der einzige erzählende Teil der Briefings — ohne ihn
   * bleibt das Reise-Briefing ein Zahlenkopf, und das ist dort ehrlich so
   * beschrieben.
   *
   * Entstehen kann er auf Knopfdruck (`warumEntwurf` weiter unten), gespeichert
   * wird er **nur** über diesen gewöhnlichen Weg: `PUT /stops` mit dem Feld
   * daran, aus dem Stationsblatt heraus über `onChange`. Der Entwurfs-Endpunkt
   * schreibt nichts.
   *
   * **Muss beim Speichern mitgeschickt werden** — und das ist der Grund, aus
   * dem das Feld überhaupt im Typ steht. `PUT /stops` ersetzt die Liste
   * vollständig; ein Feld, das der Typ nicht kennt, schickt dieser Client auch
   * nicht mit, und der Wert überlebte jede Web-Speicherung nur noch über die
   * Feldrettung in `db.replace_stops`. Ein per Claude gesetzter Satz hinge
   * damit an einer Notbremse — dieselbe Lage, in der `cost` und `booking_ref`
   * jahrelang waren.
   *
   * `null` heißt: kein Satz. Optional, weil eine Antwort aus der Zeit vor der
   * Spalte das Feld nicht mitbringt.
   */
  warum?: string | null;
  /**
   * Womit ein Abstecher erreicht wird — bei jeder anderen Art `null`.
   *
   * Optional und nicht Pflicht: Reisen, die vor dem 22.8.2026 gespeichert
   * wurden, kennen das Feld nicht, und ein Server, der es nicht mitschickt,
   * darf keinen Typfehler auslösen.
   */
  verkehrsmittel?: Verkehrsmittel | null;
  /**
   * Stufen auf dem Weg dorthin — `keine`, `wenige`, `viele` oder `null`.
   *
   * Das einzige Stück Gehbarkeit, das nirgends sonst herkommt. Die Strecke
   * rechnet der Router, die Höhenmeter auch (`fussweg.summe.aufstieg_m`) —
   * eine Treppe erkennt er nicht, und für einen Menschen, dem zwölf
   * Höhenmeter über eine Rampe leichtfallen und über einen Treppenaufgang
   * nicht, ist genau das die Auskunft, an der ein Tag hängt.
   *
   * Eine Kategorie und keine Zahl: „siebzehn Stufen" weiß niemand, „viele"
   * schon — und eine erfundene Genauigkeit wäre hier schlimmer als eine grobe
   * Wahrheit. `null` heißt „nicht angegeben" und ausdrücklich nicht „keine":
   * Der Bestand darf leer bleiben, ohne dass daraus eine Zusage wird.
   *
   * Optional wie `verkehrsmittel` und aus demselben Grund: Reisen von vor dem
   * 30.8.2026 kennen das Feld nicht.
   */
  stufen?: Stufen | null;
  /**
   * Hin- und Rückweg eines Ausflugs — nur bei `kind: 'ausflug'` belegt.
   *
   * Gerechnet wird das **serverseitig** (`POST /trips/{id}/legs` rechnet die
   * Ausflüge mit); dieser Client schickt das Feld beim `PUT /stops` nur
   * unverändert zurück. Genau deshalb steht es im Typ: `PUT /stops` ersetzt
   * die Liste vollständig, und ein Feld, das der Typ nicht kennt, schickt
   * dieser Client auch nicht mit — die gerechnete Strecke überlebte jede
   * Web-Speicherung dann nur noch über die Feldrettung in `db.replace_stops`.
   * Dieselbe Lage, in der `cost` und `warum` einmal waren.
   *
   * `zurueck: null` heißt **gespiegelt** (dieselbe Strecke zurück), `null`
   * heißt **noch nicht gerechnet** — und das ist keine Null: Der Scheduler
   * warnt in diesem Fall mit `ausflug-ohne-route`, statt den Ausflug still mit
   * null Kilometern zu verbuchen.
   */
  ausflug_leg?: AusflugLeg | null;
  /**
   * Der wievielte Tag des Aufenthalts am Träger, 1-basiert — `null` heißt
   * Ankunftstag.
   *
   * Ohne dieses Feld ließe sich ein Ausflugstag gar nicht benennen: Eine
   * Station steht im Plan nur an ihrem Ankunftstag, und fünf Nächte im
   * Ledrotal tragen zwei Ausflüge an zwei verschiedenen Tagen. Die
   * Klemmregel — ein „Tag 7" an fünf Nächten landet auf dem letzten Tag des
   * Aufenthalts — steht in `planner/scheduler.ausflugsversatz` und gilt für
   * Anzeige und Rechnung gleichermaßen.
   */
  ausflug_tag?: number | null;
  /**
   * Eigene Tagesgrenzen ab dieser Station — Tagesbeginn, Tagesende,
   * Höchstfahrzeit, Höchststrecke, jeder Teilwert einzeln.
   *
   * **Dieselbe Semantik wie die Kapitelmarke**, und deshalb steht sie neben
   * ihr: Nur die Station trägt die Marke, ab der sie gilt; alle folgenden
   * gehören über die **Reihenfolge** dazu, bis die nächste Marke kommt. Die
   * Rechnung steht einmal in `planner/scheduler.grenzenFuer`, und wer an der
   * zweiten Station der Heimfahrt kein Profil liest, hat keine Lücke gefunden,
   * sondern die Regel.
   *
   * Dreiwertig: `null` heißt keine Marke, `{}` heißt „ab hier wieder die
   * Reisevorgabe" — eine Aussage und kein Nichts. Ein fehlender **Teilwert**
   * heißt dagegen „wie die Reise" für genau diesen Wert; das Profil ist
   * ausdrücklich nicht kumulativ, ein zweites ersetzt das erste vollständig.
   *
   * Nur an **Kettenstationen**: An einem Abstecher oder Ausflug beginnt kein
   * Tag, der Server antwortet dort mit 422 und einem lesbaren Satz. An einer
   * übersprungenen Station ruht die Marke, ohne dass sie verloren geht.
   *
   * **Muss beim Speichern mitgeschickt werden** — derselbe Grund wie bei
   * `warum` und `ausflug_leg`: `PUT /stops` ersetzt die Liste vollständig, und
   * ein Feld, das der Typ nicht kennt, schickt dieser Client auch nicht mit.
   * Ein per Werkbank gesetztes Profil hinge sonst an der Feldrettung in
   * `db.replace_stops`.
   *
   * Optional wie `verkehrsmittel`: Ein Server aus der Zeit vor der Spalte
   * schickt das Feld nicht mit, und das darf keinen Typfehler auslösen.
   */
  tagesprofil?: Tagesprofil | null;
  /**
   * Ab hier beginnt ein Kapitel — „Ligurien".
   *
   * **Nur die erste Station eines Kapitels trägt den Namen.** Alle folgenden
   * gehören über die **Reihenfolge** dazu, bis die nächste Marke kommt; die
   * Regel steht einmal in `lib/kapitel.ts` (`kapitelVon`) und spiegelt
   * `main._kapitel_von` im Backend. Wer an der zweiten Station Liguriens
   * `kapitel: null` liest, hat deshalb keine Lücke gefunden, sondern die
   * Regel — die Marke trägt die erste.
   *
   * Dasselbe Muster wie beim Abstecher und aus demselben Grund: `PUT /stops`
   * ersetzt die Liste, jede Station bekommt neue Kennungen. Eine Gruppe mit
   * Verweisen wäre nach dem ersten Speichern eine Sammlung toter Zahlen;
   * Umbenennen ist so EIN Feld, und Umsortieren verhält sich von selbst
   * richtig.
   *
   * `null` heißt: keine Marke. Höchstens 80 Zeichen — der Server antwortet
   * sonst mit 422 und einem lesbaren Satz. Optional wie `verkehrsmittel`:
   * Ein Server, der das Feld nicht mitschickt, darf keinen Typfehler auslösen.
   */
  kapitel?: string | null;
  /** IANA-Zeitzone, vom Server aus den Koordinaten bestimmt. */
  tzname?: string | null;
  /**
   * Wo an dieser Station geschlafen wird.
   *
   * Zwei Bedeutungen in einem Feld: die **Ortskategorie** des gewählten
   * Schlafplatzes (`camping`, `stellplatz`, `hotel`) und die **ausdrückliche
   * Ansage** aus dem Schlafreiter. `fahrzeug` gibt es nur als Ansage — es
   * benennt keinen Ort, sondern sagt „diese Nacht im Auto", unabhängig davon,
   * was die Reise vorgibt. `null` heißt: wie die Reise.
   */
  sleep_kind?: 'hotel' | 'camping' | 'stellplatz' | 'privat' | 'fahrzeug' | null;
  /**
   * Lädt es hier über Nacht? Anschlussleistung in kW, `null` heißt nein.
   *
   * Stand bis zum Hotelnacht-Bündel in `trip.settings`, nach Stationsnummer —
   * und wanderte damit beim Einfügen einer Station an die falsche Nacht. Jetzt
   * hängt die Angabe an der Station selbst. Nur Auto-Nächte, die an keiner
   * Station hängen, stehen weiterhin in `settings`
   * (`planner/quartierstrom.ts`).
   */
  ac_kw?: number | null;
  url?: string | null;
  /**
   * Telefonnummer. Steht im Backend längst in der Spalte und kommt bei
   * POI-Übernahme mit — der Typ kannte sie nur nicht, weshalb sie nirgends
   * ankam. Auf einem Ausdruck ist sie das Nützlichste überhaupt: Wer abends
   * vor einem geschlossenen Schlagbaum steht, braucht keine Website.
   */
  phone?: string | null;
  poi_id?: number | null;
  /**
   * Ausweichquartiere für diese Übernachtung, nach Vorrang geordnet.
   *
   * **Muss beim Speichern mitgeschickt werden.** `PUT /stops` ersetzt die
   * Liste vollständig; ein Client, der das Feld wegließe, verlöre die
   * Quartiere. Der Server rettet zwar, was er wiedererkennt (siehe
   * `db.replace_stops`), aber sich darauf zu verlassen wäre die falsche
   * Richtung.
   */
  alternatives?: Quartier[] | null;
  /**
   * Eingehängte Tagespläne samt Fortschritt.
   *
   * Der Plan selbst steht woanders — hier hängt nur der Verweis und das, was
   * abgehakt ist. Muss beim Speichern mitgeschickt werden, sonst rettet der
   * Server es nur über die Notbremse in `replace_stops`.
   *
   * Der gerechnete Fußweg hängt aus demselben Grund hier wie der Fortschritt:
   * Eine Vorlage kann in mehreren Reisen stecken, der Rundweg gehört aber zu
   * genau einer Autostation. Der Schlüssel ist **additiv** — wer ihn nicht
   * kennt, reicht ihn unverändert weiter (siehe `lib/fussweg.ts`).
   */
  day_plans?: EinhaengungMitFussweg[] | null;
  /**
   * Der Fußweg der **Abstecher-Kette** dieser Station — Rundweg ab dem Auto.
   *
   * Dieselbe Form wie an einer Einhängung (`{summe, abschnitte, gerechnet_am}`)
   * und aus demselben Grund an der Station gespeichert: Der Weg beginnt und
   * endet dort, wo der Wagen steht. Er hängt nur an keinem Plan, weil es hier
   * keinen gibt — die Reihenfolge ist die der Fuß-Abstecher in der
   * Stationsliste.
   *
   * Zählt **nicht**, sobald eine Einhängung dieser Station eine eigene
   * Rechnung trägt; die Vorrangregel steht in `lib/fussweg.ts`
   * (`fusswegquelle`) und gilt für Web, App und Tagessumme gleichermaßen.
   * Gelöscht wird dabei nichts — ein ruhender Wert kommt zurück.
   *
   * Optional, weil eine Antwort aus der Zeit vor der Spalte ihn nicht
   * mitbringt. `null` heißt: noch nichts gerechnet.
   */
  fussweg?: GespeicherterFussweg | null;
  /** Verknüpfte Kajaktour im Paddle-Logger. */
  paddle_route_id?: number | null;
  /** Fixierte Ankunft (ISO). Der Scheduler warnt, wenn er sie verfehlt. */
  arrival_lock?: string | null;
  /**
   * Tatsächliche Ankunft (ISO) — er war hier. `null` heißt: noch nicht.
   *
   * Nicht zu verwechseln mit `arrival_lock` darüber: Das ist eine **Vorgabe**
   * an den Plan („um 15 Uhr muss ich an der Fähre sein"), dieses hier eine
   * **Feststellung** über die Vergangenheit. Der Scheduler rechnet mit dem
   * einen und darf das andere nie anfassen.
   *
   * Die Spalte gibt es seit M8 und sie stand bis zum 21.8.2026 in keiner
   * Zeile Code. Geschrieben wird sie über den gewöhnlichen stops-PUT — setzen
   * wie löschen; die Zustandsableitung dazu steht in
   * `planner/stationszustand.ts`.
   */
  actual_arrival?: string | null;
  /**
   * Die Buchungsnummer der Unterkunft — der Rückweg von der Buchung zum Plan.
   *
   * Die Spalte gibt es im Backend seit jeher (`STOP_FIELDS`), und die
   * iOS-Ansicht zeigt sie; im Web kannte sie nur der Typ nicht, weshalb sie
   * hier weder ankam noch geschrieben wurde. Das ist die stillere Hälfte des
   * Booking-Absprungs: Wer dort gebucht hat, hat eine Nummer in der Mail und
   * sonst nirgends — und an der Rezeption fragt niemand nach dem Reiseplan.
   *
   * Ein leeres Feld wird als `null` geschickt und nicht als `''`: Der Server
   * liest `null` als gelöscht (siehe `test_alternativen.py`), eine leere
   * Zeichenkette wäre eine gespeicherte Leere.
   */
  booking_ref?: string | null;
  /**
   * Der Preis **pro Nacht** in Euro — der Planteil der Übernachtungskosten.
   *
   * Die Spalte gibt es seit M0 und der Rechenkern deutet sie seit jeher als
   * Nachtpreis (`planner/ausgaben.ts`: `cost × nights`). Im Web fehlte sie
   * bis heute **im Typ**, und das war mehr als eine Lücke in der Anzeige: Ein
   * Feld, das der Typ nicht kennt, schickt der Client beim `PUT /stops` auch
   * nicht mit — und der Aufruf ersetzt die Liste vollständig. Der Wert
   * überlebte jede Web-Speicherung nur, weil `db.replace_stops` rettet, was
   * es wiedererkennt. Ein per MCP gesetzter Nachtpreis hing damit an einer
   * Notbremse.
   *
   * `null` heißt: kein Preis hinterlegt. Nicht `0` — eine Null wäre die
   * Angabe „kostenlos", und die ist auf einem Campingplatz eine andere
   * Aussage als „weiß ich nicht".
   */
  cost?: number | null;
  /**
   * Der Link zur Buchung **dieser** Nacht — nicht die Website des Platzes.
   *
   * Zwei Felder und nicht eines: `url` ist der Platz (die Seite des
   * Campingplatzes, die auch nächstes Jahr noch gilt), `buchung_url` das
   * Dokument dieser einen Buchung — die Bestätigungsseite bei Booking, der
   * Link aus der Mail. Wer beides in ein Feld legt, hat nach der zweiten
   * Reise an denselben Ort keine Ahnung mehr, welches von beidem dort steht.
   *
   * Geschrieben wird das Feld über den gewöhnlichen stops-PUT. Der Server
   * plausibilisiert es (`_buchung_url_pruefen`): nur `http`/`https`, höchstens
   * 500 Zeichen, keine Leer- und Steuerzeichen — und er ergänzt **kein**
   * fehlendes Schema. `www.booking.com/…` kommt also als 422 zurück und nicht
   * als stillschweigend zurechtgebogener Link.
   *
   * `null` heißt: kein Link. Optional, weil eine Antwort aus der Zeit vor der
   * Spalte das Feld nicht mitbringt.
   */
  buchung_url?: string | null;
  /**
   * Die Buchungsbelege (PDFs) — **nur lesend**.
   *
   * Der Server nimmt die Liste über diesen Weg nicht entgegen (`StopIn` kennt
   * sie nicht), und dieser Client schickt sie deshalb gar nicht erst mit:
   * `putStops` siebt sie aus, die Begründung steht in `lib/anhaenge.ts`.
   * Geschrieben wird ausschließlich über `anhangHochladen` und
   * `anhangEntfernen`; beide antworten mit der vollständigen neuen Liste,
   * damit die Oberfläche sie übernehmen kann, ohne die Reise neu zu laden.
   *
   * `null` und `[]` sind hier dasselbe: keine Belege.
   */
  anhaenge?: Anhang[] | null;
  /**
   * Sitzt diese Station da, wo sie hinsoll? — das Prüfergebnis (V1).
   *
   * **Muss beim Speichern mitgeschickt werden.** Die ausführliche Begründung
   * steht am Typ `Ortsschaerfe`; kurz: `PUT /stops` ersetzt die Liste, ein
   * Feld ohne Typ geht nicht mit, und der Befund hinge an der Feldrettung in
   * `db.replace_stops`.
   *
   * `null` heißt: nie geprüft. Ausdrücklich nicht „in Ordnung" — genau diesen
   * Unterschied zeigt der Vorbereitung-Reiter.
   */
  ortsschaerfe?: Ortsschaerfe | null;
  /**
   * Wohin stattdessen, wenn es hier nicht geht (V4).
   *
   * **Muss beim Speichern mitgeschickt werden**, derselbe Grund wie oben.
   * Genannt wird es vom Briefing nur, wenn die Lage vor Ort einen Anlass
   * liefert (`status != frei`) — ein Plan B ohne Grund wäre eine Angabe ohne
   * Anlass.
   */
  ausweichziel?: Ausweichziel | null;
}

export interface Trip {
  id: number;
  name: string | null;
  description: string | null;
  begin_date: string | null;
  end_date: string | null;
  /** Tagesrhythmus als Millisekunden seit lokaler Mitternacht. */
  day_start_ms?: number;
  day_end_ms?: number;
  max_drive_ms?: number | null;
  max_dist_m?: number | null;
  stopover_ms?: number;
  round_trip?: boolean;
  /**
   * Die beiden Achsen der Reiseart — sie hängen an der Reise, nicht am Konto.
   *
   * Derselbe Mensch fährt im Mai mit dem E-Auto ins Hotel und im August mit
   * dem Bulli auf den Stellplatz. Beide Felder sind optional, weil eine Reise
   * aus der Zeit vor den Achsen sie nicht mitbringt; ohne Angabe gilt die
   * Vorgabe der Spalte (`elektro` / `fahrzeug`), also das bisherige Verhalten.
   * Ausgewertet wird das nicht hier, sondern in `reiseart.ts`.
   */
  antrieb?: 'elektro' | 'verbrenner';
  uebernachtung?: 'fahrzeug' | 'unterkunft' | 'gemischt';
  /**
   * Die Währungen, die auf dieser Reise vorkommen — `["EUR","USD","CAD"]`.
   *
   * Der Server bildet sie aus den Ländern der Stationen und legt Euro dazu;
   * gerechnet wird weiterhin in Euro. Gebraucht wird die Liste an genau einer
   * Stelle: als Vorschlagsliste am Währungsfeld im Kostenreiter.
   *
   * Optional, weil ein älterer Server sie nicht mitschickt. Dann bleibt EUR
   * übrig (`kosten.waehrungsliste`) — das ist der bisherige Zustand und kein
   * Fehler.
   */
  waehrungen?: string[] | null;
  /**
   * Wann die Reise abgeschlossen wurde — `null` heißt: sie ist offen.
   *
   * Ein Zeitstempel und kein Boolean, und zwar auf der ganzen Strecke: `TRUE`
   * beantwortet die Frage „seit wann liegt das hinter mir" nie mehr. Geschaltet
   * wird trotzdem mit einem Boolean (`TripPatch.abgeschlossen`) — die
   * Oberfläche hakt ab, die Datenbank merkt sich wann.
   *
   * Der Schalter ordnet nur die Übersicht. Eine abgeschlossene Reise bleibt in
   * vollem Umfang bearbeitbar, und die geteilte Seite weiß nichts davon.
   *
   * Optional, weil eine Antwort aus der Zeit vor der Spalte sie nicht
   * mitbringt; fehlt sie, gilt die Reise als offen.
   */
  abgeschlossen_at?: string | null;
  /**
   * Der freie Bereich der Reise — ein JSON-Block, den der Server **als Ganzes**
   * ersetzt.
   *
   * Darin liegen nebeneinander, jeder mit seinem eigenen Leser: `road_prefs`
   * (Straßenvorgaben der Route), `ladevorgaben` (die fünf Zahlen des
   * Reiseprofils, `reiseprofile.ladevorgabenAus`), `ladeplan` (die vier Regler
   * der Ladeplanung, siehe unten), `quartier_ac_kw` (Wallbox der Auto-Nächte,
   * `planner/quartierstrom.ladenAmQuartier`), `quartier`, `kajak`, `budget`,
   * `merkmale`, `vignetten`, `titelbild` und der Profilvermerk.
   *
   * **`ladeplan`** ist seit dem 12.9.2026 dabei (Vertrag § 1.4):
   * `{start_soc, ziel_soc, standverbrauch_kw, temperatur_c, temperatur_quelle}`.
   * Die vier Regler waren bis dahin reiner Browserzustand — wer im Web mit
   * 60 % Startladung plante, bekam über MCP einen Ladeplan mit 80 %, und
   * niemand konnte sehen, warum. Gelesen wird der Block im Kern
   * (`planner/eingabe.ladeplanAus`, mit `LADEPLAN_VORGABEN` als Rückfall),
   * geschrieben in `ladeplan.mitLadeplan`.
   *
   * Der Typ bleibt `Record<string, unknown>` und wird es bleiben: Jeder Block
   * hat seinen eigenen tolerant lesenden Übersetzer, und eine Typzeile hier
   * verspräche eine Prüfung, die niemand macht — die Antwort kommt von drei
   * Serverständen.
   */
  settings?: Record<string, unknown> | null;
  rev: number;
  stop_count?: number;
  /**
   * Erste und letzte nicht übersprungene Station — nur in der Reiseliste.
   *
   * Damit lässt sich eine namenlose Reise als „Bernau → Ledro" anschreiben,
   * ohne für jeden Eintrag die Stationen nachzuladen.
   */
  erste_station?: string | null;
  letzte_station?: string | null;
  /**
   * Der Anzeigename des Besitzers — **nur bei fremden Reisen**, sonst `null`.
   *
   * Die Reiseliste enthält seit dem Mitplanen beides in einer Sortierung. Der
   * Server setzt das Feld genau dann, wenn die Reise jemand anderem gehört; die
   * Oberfläche braucht deshalb keine Regel dafür, wann sie „von mir" zu
   * verschweigen hat. Optional, weil eine ältere Antwort es nicht mitbringt —
   * dann gilt „eigene Reise", also das Verhalten von vorher.
   */
  mitplaner_von?: string | null;
  /**
   * Die Anzeigenamen der Mitplaner — nur bei **eigenen** Reisen gefüllt.
   *
   * Damit die Zeile „mit Thomas" sagen darf. Bei einer fremden Reise steht hier
   * nichts: Wer sonst noch mitplant, ist eine Auskunft, die der Besitzer gibt.
   */
  mitplaner?: string[];
  /**
   * Das Titelbild der Reise — **nur in der Reiseliste**, und schon aufgelöst.
   *
   * Zwei Zahlen und kein Bild: Der private Ladeweg geht über
   * `medienHolen(tripId, eintrag, medium, true)`, weil der Sitzungstoken nicht
   * in eine Adresse gehört (siehe dort). Der Server wählt das jüngste Foto
   * eines **geteilten** Eintrags und lässt sich davon durch
   * `settings.titelbild` übersteuern; welches von beidem gilt, ist hier schon
   * entschieden (`lib/titelbild.ts` rechnet den Vorrang ausdrücklich nicht
   * nach).
   *
   * `null` heißt: Diese Reise hat kein Bild vorzuzeigen — dann steht die Zeile
   * da wie vor dieser Etappe. Und dass ein Bild genannt wird, heißt nicht,
   * dass es noch existiert: Der Server prüft das nicht, ein gelöschtes Foto
   * ergibt beim Laden einen 404, und der wird still behandelt.
   */
  titelbild_eintrag?: number | null;
  titelbild_medium?: number | null;
  stops?: Stop[];
}

/**
 * Die Antwort auf `POST /trips/{id}/duplicate`: die neue Reise, und bei
 * mitkopierten Belegen zwei Zahlen flach daneben.
 *
 * Beide sind optional, weil sie **nur** bei `belege: true` mitkommen — was mit
 * ihnen anzufangen ist, weiß `kopiermeldung` in `kopieren.ts`.
 */
export interface Kopie extends Trip, Belegbilanz {}

/* ══ Mitplanen ═══════════════════════════════════════════════════════════
 *
 * Zwei Konten an einer Reise. Die Formen stehen hier, weil sie beschreiben,
 * was der Server sagt — die Flächen dazu sind `Mitplaner.svelte` (Besitzer und
 * Mitplaner im Teilen-Reiter) und `Mitplanen.svelte` (die Seite hinter dem
 * Link).
 */

/** Ein Konto, das an dieser Reise mitplant. Ohne E-Mail — der Name genügt. */
export interface Mitplaner {
  user_id: number;
  name: string | null;
  created_at: string | null;
}

/** Ein noch offener Mitplaner-Link. Ohne Adresse — die gibt es genau einmal. */
export interface Mitplanereinladung {
  id: number;
  created_at: string | null;
  expires_at: string | null;
}

/** Die Antwort auf `GET /trips/{id}/mitplaner`. Antwortet Mitplanern mit 404. */
export interface Mitplanerstand {
  mitplaner: Mitplaner[];
  einladungen: Mitplanereinladung[];
}

/** Was hinter einem Mitplaner-Link steckt — ohne Anmeldung abfragbar. */
export interface Mitplanenangebot {
  reise_name: string | null;
  von: string | null;
  gilt_bis: string;
}

/**
 * Das Ergebnis des Einlösens.
 *
 * `schon_dabei` ist kein Fehler, sondern der Normalfall beim zweiten Klick:
 * Wer den Link gebookmarkt hat oder auf „zurück" tippt, ist längst drin und
 * soll das auch lesen.
 */
export interface Mitplanenergebnis {
  ok: boolean;
  trip_id: number;
  reise_name: string | null;
  von: string | null;
  schon_dabei: boolean;
}

/**
 * Was `PATCH /trips/{id}` entgegennimmt.
 *
 * Fast deckungsgleich mit `Trip` — bis auf `abgeschlossen`, das als einziges
 * Feld hineingeht und nicht wieder heraus: Der Server macht daraus
 * `abgeschlossen_at`. `true` setzt den Zeitstempel, `false` nullt ihn,
 * weggelassen fasst ihn nicht an — dieselbe Nur-übergebene-Felder-Semantik wie
 * beim Rest des Aufrufs.
 */
export type TripPatch = Partial<Trip> & { abgeschlossen?: boolean };

/** Eine berechnete Etappe. Gehört zur Zielstation (`to_ord`), wie Furkots `expander`. */
export interface Leg {
  to_ord: number;
  distance_m: number | null;
  duration_ms: number | null;
  /** Google-Polyline. `null`, wenn keine Route gefunden wurde — dann zeigt die Karte die Luftlinie. */
  polyline: string | null;
  provider: string | null;
  routing: string;
  /**
   * Mautpflichtige Meter dieser Etappe, vom Router (`tollways`).
   *
   * **`null` heißt unbekannt, `0` heißt geprüft mautfrei.** Der Unterschied ist
   * die halbe Wahrheit der Mautschätzung, und beide als „keine Maut" zu lesen
   * ist der teure Fehler: Eine Reise, deren Route vor der Kostenautomatik
   * gerechnet wurde, trägt überall `null` — sie wäre dann eine Reise ohne
   * Mautkosten, obwohl sie durch halb Italien führt. Frisch wird der Wert nach
   * `route_berechnen`; bis dahin steht er als offener Posten in der
   * Kostenübersicht (`vorbereitung.mautkosten`).
   *
   * Optional, weil ältere Serverstände das Feld gar nicht senden — auch das
   * heißt unbekannt und nicht null Meter.
   */
  toll_m?: number | null;
  /**
   * Höhenmeter dieser Etappe, wie das Backend sie in `legs.elevation` ablegt.
   *
   * Sie standen hier bis zum 12.9.2026 nicht im Typ, obwohl der Server sie
   * seit Langem mitschickt — gelesen wurden sie nur über einen Cast
   * (`VorbereitungPanel`, `planner/achse.ts`). Seit der Kern die Etappe selbst
   * übersetzt (`planner/eingabe.legAus`), gehört das Feld in den Typ:
   * Dreitausend Höhenmeter über den Brenner sind gut zehn Prozent Verbrauch,
   * und ein Feld, das nur per Cast existiert, verschwindet beim nächsten Umbau
   * still.
   */
  elevation?: Hoehenprofil | null;
  /**
   * Wann diese Etappe gerechnet wurde — der Zeitstempel aus der Spalte.
   *
   * Er ist **Teil des Plan-Stands** (`planner/eingabe.standAus`): Eine neu
   * gerechnete Route macht jeden vorhandenen Plan veraltet. Wer die Etappen an
   * den Kern weitergibt, ohne ihn mitzunehmen, bekommt einen Stand, der von
   * dem des Servers abweicht — und damit einen Plan, den der Server bei jedem
   * Abruf neu rechnet, ohne dass irgendwo etwas aufleuchtet.
   *
   * Optional, weil ein Server ohne die Spalte das Feld nicht schickt.
   */
  computed_at?: string | null;
}

/**
 * Was das Rechnen der Ausflüge ergeben hat — der zweite Bericht von
 * `POST /trips/{id}/legs`.
 *
 * `gerechnet` sind die **Namen** der Ausflüge, deren Hin- und Rückweg jetzt an
 * der Station stehen; `offen` sind fertige **Sätze** über das, was nicht ging
 * („… der Rückweg war nicht zu rechnen — gerechnet wird mit der gespiegelten
 * Hinstrecke."). Fertige Sätze und keine Codes, weil der Server die Gründe
 * kennt und die Oberfläche sie sonst ein zweites Mal formulieren müsste —
 * dieselbe Fehler-als-Satz-Konvention wie überall im Haus.
 *
 * Optional, weil ein Serverstand aus der Zeit vor dem Ausflug das Feld nicht
 * mitschickt. Fehlt es, gab es nichts zu rechnen.
 */
export interface Ausflugsbericht {
  gerechnet?: string[];
  offen?: string[];
}

export interface LegResult {
  legs: Leg[];
  stats: { cache: number; neu: number; fehler: number };
  /**
   * Die Ausflüge dieser Reise, mitgerechnet.
   *
   * Sie stehen **nicht** in `legs`: Eine Etappe trägt den Schlüssel
   * `(trip_id, to_ord)` und beschreibt die Kette, ein Ausflug ist keine Kette.
   * Seine beiden Richtungen landen an seiner Station (`Stop.ausflug_leg`).
   *
   * **Der Aufruf kann dabei die Revision der Reise erhöhen** — er schreibt die
   * gerechneten Strecken über den gewöhnlichen Stationsweg zurück. Wer die
   * Stationen danach unverändert weiterbenutzt, speichert mit einer veralteten
   * Revision und bekommt zu Recht einen Zusammenstoß gemeldet.
   */
  ausfluege?: Ausflugsbericht;
}

/** Ein Ort aus dem POI-Bestand (OSM-Import, später auch eigene Favoriten). */
export interface Poi {
  id: number;
  source: string;
  name: string;
  address: string | null;
  lat: number;
  lon: number;
  category: string;
  subcategory: string;
  url: string | null;
  phone: string | null;
  attrs: Record<string, string> | null;
  /** 0–100 aus Wikidata-Sprachversionen und Artikellänge. `null` = noch nicht geprüft. */
  prominence: number | null;
  /** Einleitung des Wikipedia-Artikels. */
  summary: string | null;
  /** Abstand zur Route in Metern (Korridorsuche) bzw. zum Mittelpunkt (Umkreis). */
  abstand_m: number | null;
  /** Geschätzter Umweg hin und zurück. Grundlage der Sortierung. */
  umweg_ms: number | null;
}

export interface Freigabe {
  id: number;
  name: string | null;
  zeigt_standort: boolean;
  zeigt_kommende: boolean;
  zeigt_quartiere: boolean;
  created_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  last_seen_at: string | null;
  aufrufe: number;
  /**
   * Wie viele Geräte über diesen Link mitlesen — eine Zahl, keine Liste.
   *
   * Der Server schickt sie laut Vertrag **immer** mit, notfalls als `0`; ein
   * Feld, das nur bei Bedarf erschiene, zwänge jede Anzeige zu einer
   * Unterscheidung zwischen „niemand“ und „diese Serverfassung kennt das
   * nicht“. Freiwillig steht es hier trotzdem: Ein Browser, der noch die alte
   * Auskunft im Cache hat, soll nicht am Typ scheitern.
   *
   * Wer mitliest, steht bewusst nirgends — in der Abo-Tabelle des Servers gibt
   * es keinen Namen und keine Adresse. Gelesen wird das in `mitlesen.ts`.
   */
  abos?: number;
}

export interface Rueckmeldung {
  id: number;
  ziel_art: 'eintrag' | 'station' | 'tagesplan' | 'reise';
  ziel_id: number | null;
  name: string | null;
  text: string | null;
  emoji: string | null;
  created_at: string;
  verborgen: boolean;
  gelesen: boolean;
  /**
   * Name der Freigabe, über die es hereinkam — „Papa", „Susanne".
   *
   * Die Zuordnungshilfe für Reaktionen: Dort tippt niemand einen Namen ein.
   * Nur in der eigenen Ansicht; auf der geteilten Seite steht sie nicht.
   */
  link_name: string | null;
}

export type { Reisebriefing, Tagesbriefing, Warumentwurf } from './briefing';
export type { Tagebucheintrag, Medium, Verweis } from './tagebuch';
export type { Kostenstand } from './kosten';
export type { Anhang } from './anhaenge';

/**
 * Die Antwort auf einen Anhang-Upload.
 *
 * Sie trägt beides: den **einen** neuen Eintrag („was ist angekommen“) und die
 * **ganze** Liste der Station („wie sieht es jetzt aus“). Ohne die zweite
 * müsste die Oberfläche die Stationsliste neu laden, nur um einen Namen
 * anzuzeigen — mitten in einer Reise, an der gerade jemand arbeitet.
 */
export interface AnhangAntwort {
  ord: number;
  station: string | null;
  anhang: Anhang;
  anhaenge: Anhang[];
  /** Der Abrufweg, so wie der Server ihn nennt: `/anhang/<id>.pdf`. */
  url: string;
}

export interface GeoHit {
  label: string;
  name: string;
  lat: number;
  lon: number;
  kind?: string;
  country?: string;
  /**
   * Der Treffer ist eine Straße oder eine Fläche, sein Punkt also ein
   * Mittelwert — nicht der Ort, den jemand meint.
   *
   * Genau das war die Salò-Falle: „Via Angelo Landi" ist ein Straßenabschnitt,
   * sein Punkt liegt in der Mitte, und das Parkhaus stand 127 m daneben. Der
   * Server setzt beide Felder **immer**, auch als `false` und `null`
   * (`ortsschaerfe.treffer_marke`); optional sind sie hier nur, weil ein
   * Server aus der Zeit davor sie nicht mitschickt.
   */
  unscharf?: boolean;
  /** Der Satz dazu — „Straßentreffer: der Punkt ist die Mitte …". */
  hinweis?: string | null;
}

/**
 * Die Maße des Wagens — für Parkhäuser und Fähren.
 *
 * **Nur Anzeige.** Gerechnet wird damit nichts: Der Router kennt keine
 * Durchfahrtshöhen, und eine Höhenangabe, aus der eine Umleitung entstünde,
 * wäre ein Versprechen ohne Datengrundlage. Die einzige Ableitung ist ein
 * **Satz** in der Ortsschärfe-Zeile an einem Parkhaus, wenn `hoehe_m` über
 * 2,0 liegt (Vertrag § 1.5).
 */
export interface Fahrzeugmasse {
  laenge_m: number | null;
  breite_m: number | null;
  hoehe_m: number | null;
}

/** Was das Fahrzeug übers Übernachten weiß. */
export interface Fahrzeugcamping {
  /**
   * Verbrauch einer Nacht im Fahrzeug, in kWh.
   *
   * Gesetzt ersetzt er im Rechenkern die Pauschale von 0,8 kW × Stunden.
   * `null` heißt: Es gilt weiter die Pauschale — nicht „null kWh".
   */
  standverbrauch_kwh_nacht: number | null;
  /** Kann der Wagen 230 V abgeben? Bedingung des Packpostens V2L-Adapter. */
  v2l: boolean | null;
  /** Untere SoC-Grenze am Abend, in Prozent. `null` heißt: keine eigene Grenze. */
  soc_reserve: number | null;
}

/**
 * Winterpflichten am Fahrzeug.
 *
 * Die beiden Daten stehen als `MM-DD` und **ohne Jahr**: Eine Winterreifen-
 * Pflicht ist eine wiederkehrende Frist, kein Termin. Ihr einziger Leser ist
 * die Passprüfung, die daraus eine Zeile im Prompt macht.
 */
export interface Fahrzeugwinter {
  winterreifen_von: string | null;
  winterreifen_bis: string | null;
  schneeketten: boolean | null;
}

/**
 * Eine ZTL-Genehmigung — die Einfahrterlaubnis für eine italienische
 * Innenstadt.
 *
 * `id` trägt der Server bei (8 Hex); beim Lesen ergänzt er fehlende. Sie ist
 * der Griff, über den der Editor einen Posten ändert und streicht — ohne sie
 * müsste er über die Stelle in der Liste arbeiten, und die verschiebt sich,
 * sobald ein zweiter Tab etwas einträgt. Optional im Typ, weil ein Server aus
 * der Zeit davor sie nicht mitschickt.
 */
export interface ZtlGenehmigung {
  id?: string | null;
  /** Pflicht — „Milano", „Firenze". Ohne Stadt ist der Posten sinnlos. */
  city: string;
  /** ISO-Datum oder `null` für unbefristet. */
  valid_until?: string | null;
  /** Das Kennzeichen, auf das sie läuft. Leer heißt: das des Fahrzeugs. */
  plate?: string | null;
  /** Höchstens 300 Zeichen — Aktenzeichen, Portal, wo der Nachweis liegt. */
  note?: string | null;
}

/** Fahrzeug — Grundlage jeder Ladeplanung. */
export interface Vehicle {
  id: number;
  name: string;
  battery_kwh: number | null;
  consumption_kwh_100: number | null;
  consumption_factor: number;
  curb_weight_kg: number | null;
  max_dc_kw: number | null;
  max_ac_kw: number | null;
  charge_curve: { soc: number; kw: number }[] | null;
  soc_min: number;
  soc_max: number;
  connectors: string[] | null;
  // `preferred_operators` stand hier bis zu dieser Etappe und ist ausgemustert:
  // Die Spalte gibt es seit M0, gelesen hat sie nie jemand — weder die
  // Oberfläche noch der Ladeplaner, der seine Rangfolge aus den Tarifen und der
  // Leistung bildet. Im Schema ist sie als tot vermerkt. Ein Feld im Typ, das
  // nirgends vorkommt, ist kein Vorrat für später, sondern eine Zeile, die beim
  // Lesen eine Fähigkeit vortäuscht.
  /**
   * Ladetarife, wie sie in der Spalte stehen.
   *
   * Die Form kennt der Rechenkern (`TarifDraht`) und niemand sonst: Er
   * verträgt beide Fassungen — das Alt-Format `{net, eur_kwh}` der
   * Bestandsdaten und das neue mit Betreiberliste, Länderpreisen und
   * Preisstand (`ab`). Übersetzt wird ausschließlich in
   * `tarifeVonFahrzeug()`; hier steht nur, was über den Draht kommt.
   */
  tariffs: TarifDraht[] | null;
  /**
   * Die ZTL-Genehmigungen. `null` heißt **keine** — der Normalfall.
   *
   * Die Form ist seit dem 12.9.2026 breiter (`id`, `plate`, `note`); die
   * Auswertung im Planer liest weiterhin nur `city` und `valid_until`, und
   * genau deshalb sind die drei neuen Felder optional.
   */
  ztl_permits: ZtlGenehmigung[] | null;
  /**
   * Das amtliche Kennzeichen.
   *
   * Es wird nirgends gerechnet und steht dafür an vier Stellen, an denen man
   * es sonst aus dem Fahrzeugbrief holt: in der Notizvorlage einer Vignette,
   * im ZTL-Editor, am Mautzugang und im Fahrzeugblatt.
   *
   * Optional, weil ein Server ohne die Spalte das Feld nicht mitschickt.
   */
  kennzeichen?: string | null;
  /**
   * Verbrauch in l/100 km — für **Verbrenner-Reisen**.
   *
   * Das Gegenstück zu `consumption_kwh_100`, und es steht am selben Fahrzeug:
   * Der Antrieb hängt heute an der **Reise** und nicht am Auto (siehe
   * `reiseart.ts`), also trägt ein Fahrzeug beide Zahlen, und je Reise zählt
   * die passende. Gerechnet wird damit der Planposten `sprit` (Etappen-km ×
   * l/100 km × Länder-Richtwert je Liter).
   *
   * `null` heißt **nicht eingetragen** — dann steht in der Kostenübersicht
   * einer Verbrenner-Reise der offene Posten „Verbrauch am Fahrzeug
   * eintragen" statt einer geratenen Zahl.
   *
   * Optional, weil ein Server ohne die Spalte das Feld nicht schickt.
   */
  verbrauch_l_100?: number | null;
  /**
   * Benzin oder Diesel — der Richtwert hängt daran.
   *
   * Die Preise gehen um zehn Cent je Liter auseinander, und über 4000 km sind
   * das gut vierzig Euro. `null` heißt: nicht angegeben; der Server rechnet
   * dann mit dem Richtwert, den er für diesen Fall führt.
   */
  kraftstoff?: 'benzin' | 'diesel' | null;
  /** Länge, Breite, Höhe — nur Anzeige, siehe `Fahrzeugmasse`. */
  masse?: Fahrzeugmasse | null;
  camping?: Fahrzeugcamping | null;
  winter?: Fahrzeugwinter | null;
  /**
   * Vignetten und Mautzugänge, wie sie in der Spalte stehen.
   *
   * `null` heißt **keine hinterlegt** und ist der Normalfall eines Fahrzeugs,
   * an dem noch niemand etwas eingetragen hat. Die Form steht unten bei
   * `Fahrzeugvignette`; abgeleitet wird daraus nichts im Browser — den Stand
   * je Pflichtland liefert `GET /trips/{id}/vignetten`.
   *
   * Optional, weil ein Server ohne Vignetten-Wissen das Feld nicht schickt:
   * Die Fläche fällt dann auf das alte Häkchen zurück.
   */
  vignetten?: Fahrzeugvignette[] | null;
  /**
   * Wann das Fahrzeug zuletzt geändert wurde.
   *
   * Gelesen wird es an genau einer Stelle, und dort zählt es: Es ist
   * **Teil des Plan-Stands** (`planner/eingabe.standAus`). Eine neue
   * Ladekurve oder ein anderer Verbrauch ist ein anderer Ladeplan, und ohne
   * diesen Zeitstempel sähe der alte Plan weiter aktuell aus.
   *
   * Optional, weil ein Server ohne die Spalte das Feld nicht schickt — dann
   * fehlt es im Stand auf allen drei Läufern gleich.
   */
  updated_at?: string | null;
}

/** Ladesäule entlang der Route, wie der Ladeplaner sie erwartet. */
export interface LadesaeuleDto {
  id: number;
  name: string | null;
  lat: number;
  lon: number;
  betreiber: string | null;
  land: string | null;
  kw: number;
  entlang_m: number;
  umweg_ms: number | null;
  stecker: string[];
  ladepunkte: number | null;
  quelle: string;
}

/** Kajaktour aus dem Paddle-Logger. Kommt nur lesend herüber. */
export interface PaddleTour {
  id: number;
  name: string;
  distanz_m: number;
  dauer_ms: number;
  einstieg: { lat: number; lon: number };
  ausstieg: { lat: number; lon: number };
  punkte: { lat: number; lon: number }[];
  geaendert: string | null;
}

/** Gewässer mit Geometrie im Paddle-Logger. */
export interface Gewaesser {
  name: string;
  lat: number;
  lon: number;
  /** [südlichste Breite, westlichste Länge, nördlichste, östlichste] */
  bbox: [number, number, number, number];
  inseln: number;
}

/* ══ Packliste ═══════════════════════════════════════════════════════════
 *
 * Die Formen hinter `/trips/{id}/packliste` und dem `packliste`-Block an
 * `/auth/me`. Sie stehen hier, weil sie beschreiben, was der **Server** sagt;
 * die Regeln darauf (Gruppieren, Ergänzen, Streichen, Zählen) liegen in
 * `lib/packliste.ts` und sind dort ohne Browser prüfbar.
 *
 * **Die Bedingungsregel gehört dem Server.** Bis zum 11.9.2026 rechnete der
 * Browser selbst aus, welcher Posten für eine Reise gilt — und die
 * Camping-Hälfte dieser Rechnung hing am Ergebnis des Schedulers, also an
 * einer Zahl, die es nur im Browser gibt. Die App kam zu einer anderen Liste
 * als das Web, und beide sahen richtig aus. Jetzt kommt `aktiv` je Posten und
 * `bedingungen` an der Antwort; der Client liest das und leitet nichts ab.
 */

/**
 * Die drei **eingebauten** Bedingungen. Mehrere gelten zusammen (UND).
 *
 * Sie sind nicht mehr die einzigen: Seit dem 12.9.2026 trägt eine Reise
 * zusätzlich freie **Merkmale** (`hund`, `winter`, `staedte` — Vertrag § 1.3),
 * und ein Posten kann an einem davon hängen. Deshalb ist `Packposten.wenn`
 * eine Liste von Zeichenketten und nicht mehr von diesem Typ; die drei hier
 * bleiben trotzdem eigens benannt, weil sie ein deutsches Wort und eine
 * feste Reihenfolge haben (`packliste.BEDINGUNGEN`) und weil der Server sie
 * selbst rechnet, statt sie an der Reise zu lesen.
 */
export type Packbedingung = 'kajak' | 'camping' | 'elektro';

export interface Packposten {
  /** Stabil über das Speichern hinweg — der Griff für Haken und Änderungen. */
  id: string;
  bereich: string;
  was: string;
  /** Ohne das fährt man besser gar nicht los. Angezeigt als Marke „wichtig". */
  kritisch: boolean;
  /**
   * Woran der Posten hängt. `null` heißt: gilt immer.
   *
   * Eine der drei eingebauten Bedingungen (`Packbedingung`) **oder** ein
   * freies Merkmal der Reise (Kleinbuchstaben, ASCII, ≤ 20 Zeichen). Deshalb
   * `string[]` und nicht `Packbedingung[]`: Ein engerer Typ hätte bedeutet,
   * dass jedes neue Merkmal eine Codeänderung braucht — und genau das sollen
   * freie Merkmale nicht.
   */
  wenn: string[] | null;
  erledigt: boolean;
  notiz: string | null;
  /**
   * Gilt der Posten für **diese** Reise?
   *
   * Nur in den Reise-Antworten gesetzt; in der Kontofassung gibt es keine
   * Reise, für die etwas gelten könnte, und das Feld fehlt. Ein fehlendes Feld
   * heißt „ja" (siehe `packliste.istAktiv`) — die umgekehrte Vorgabe hätte im
   * Konto-Editor eine leere Liste ergeben.
   */
  aktiv?: boolean;
}

/**
 * Was diese Reise über sich sagt — die Grundlage von `aktiv`.
 *
 * Die drei eingebauten Bedingungen sind immer dabei; die freien Merkmale der
 * Reise kommen als weitere Schlüssel dazu (`hund: true`). Der Index dafür
 * steht hier und nicht als zweites Feld, weil der Server sie in derselben
 * Abbildung ausliefert — für den Leser ist „gilt camping" dieselbe Sorte
 * Frage wie „gilt hund".
 */
export interface Packbedingungen {
  kajak: boolean;
  camping: boolean;
  elektro: boolean;
  [merkmal: string]: boolean;
}

export interface Packlistenstand {
  posten: Packposten[];
  /** Zeitstempel der letzten Änderung, oder `null` bei einer ungespeicherten Liste. */
  stand: string | null;
  /**
   * Steht die Liste so an der Reise?
   *
   * `false` heißt: Was hier kommt, ist abgeleitet (aus dem Konto oder aus der
   * eingebauten Vorgabe) und noch nie gespeichert worden. Der erste Haken
   * schreibt sie fest — deshalb ist das kein Fehlerzustand, sondern der
   * Normalfall einer frischen Reise.
   */
  gespeichert: boolean;
  quelle: 'reise' | 'konto' | 'vorgabe';
  bedingungen: Packbedingungen;
}

/** Die Standard-Packliste am Konto. */
export interface Kontopackliste {
  posten: Packposten[];
  quelle: 'konto' | 'vorgabe';
}

/* ══ Vignetten am Fahrzeug ═══════════════════════════════════════════════
 *
 * Die Formen hinter `GET /trips/{id}/vignetten`, `PUT /vehicles/{id}` und
 * `POST /vehicles/{id}/vignetten`. Sie stehen hier, weil sie beschreiben, was
 * der **Server** sagt; die Regeln darauf (Standwort zu Marke, Zeilentexte,
 * Vorbelegung, Formprüfung) liegen in `lib/vignetten.ts` und sind dort ohne
 * Browser prüfbar.
 *
 * **Die Deckungsregel gehört dem Server.** Ob ein Land für diese Reise gedeckt
 * ist, hängt am Gültigkeitsfenster, am Reisezeitraum und an der Landesregel
 * für Jahresvignetten — die schweizerische für 2026 läuft vom 1. Dezember 2025
 * bis zum 31. Januar 2027. Diese Regel an zwei Stellen zu pflegen hieße, zwei
 * Wahrheiten über dieselbe Dezemberreise zu haben. Deshalb kommt `stand` je
 * Pflichtland fertig herüber, und sogar die `vorbelegung` für den Knopf.
 *
 * Dieselbe Entscheidung wie bei der Packliste eine Fläche weiter unten, und
 * aus demselben Anlass: Dort rechneten Web und App verschieden, und beide
 * sahen richtig aus.
 */

/* ── Der Mautkatalog ───────────────────────────────────────────────────
 *
 * `GET /mautkatalog` — die **eine** Preis- und Regeltabelle für alle drei
 * Seiten. Das Web hält seit dem 12.9.2026 keine Kopie mehr: Die alte
 * `MAUTREGELN`-Tabelle in `planner/vorbereitung.ts` bleibt nur noch als
 * Rückfall für einen Server ohne Katalog. Zwei handgepflegte Preislisten
 * laufen auseinander, sobald eine angefasst wird — und man sieht es nicht,
 * weil sie nie nebeneinander stehen.
 *
 * Alle Beträge sind **Anhaltswerte in Euro, Stand 2025/2026**; Fremdwährung
 * ist umgerechnet. Jedes Land trägt seinen `stand`, damit die Zeile sagen
 * kann, wie alt sie ist.
 */

/** Zeitvignette, Streckenmaut oder für Pkw mautfrei. */
export type Mautsystem = 'vignette' | 'strecke' | 'frei';

/**
 * Wie das Jahr einer Jahresvignette liegt.
 *
 * `dezember` — 1. Dezember des Vorjahres bis 31. Januar des Folgejahres
 * (AT, CH). `rollend` — zwölf Monate ab dem Wahltag (SI, CZ, SK, BG, RO).
 * `kalenderjahr` — Kalenderjahr plus Januar des Folgejahres (HU).
 *
 * Gerechnet wird das **im Server** (`mautkatalog.gueltigkeit`). Das Wort steht
 * hier, weil die Auswahl im Dialog es anzeigt: „Jahr · 106,80 € · gilt
 * 1.12.–31.1." ist der Satz, an dem man erkennt, dass eine Dezemberreise noch
 * mit der Vignette des Folgejahres fährt.
 */
export type Jahresregel = 'dezember' | 'rollend' | 'kalenderjahr';

/** Eine kaufbare Option eines Vignettenlandes. */
export interface Mautoption {
  /** Katalogschlüssel — er geht als `option` an den Posten. */
  key: string;
  name: string;
  /** Laufzeit in Tagen bzw. Monaten. */
  tage?: number | null;
  monate?: number | null;
  /**
   * Die Jahresoption — **das Merkmal steht am Posten und wird nicht erraten**.
   *
   * Naheliegend wäre „weder Tage noch Monate, also Jahr". Genau daran scheitert
   * die bulgarische Wochenendvignette: Sie hat keine Laufzeit in Tagen, weil
   * sie am Wochenende hängt und nicht am Kauftag — und als Jahresvignette
   * gelesen bekäme sie ein vierzehnmonatiges Fenster für 5,11 €.
   */
  jahr?: boolean;
  /** Pkw-Tarif in Euro, `null` wenn der Katalog keinen nennt. */
  eur: number | null;
  /** Umgerechnet aus einer Fremdwährung — der Betrag ist ein „ca.". */
  ca?: boolean;
  /** Der Originalbetrag, wo umgerechnet wurde: `CHF 40`, `2300 CZK`. */
  original?: string | null;
}

/** Eine Sondermautstrecke oder ein Sonderbauwerk (Tunnel, Brücke). */
export interface Sondermautstrecke {
  key: string;
  name: string;
  eur: number | null;
  /** Hin und zurück, wo der Betreiber das gesondert ausweist (Mont-Blanc). */
  eur_retour?: number | null;
  ca?: boolean;
  original?: string | null;
  /** Bei einem Tunnel zwischen zwei Ländern: beide Länder. */
  laender?: string[] | null;
  stand?: string | null;
}

export interface Mautland {
  /**
   * ISO-2 — der Schlüssel steht **in** der Zeile.
   *
   * Der Server schickt `laender` als **Liste** und nicht als Tabelle, und das
   * ist die bessere Form: Sie kommt sortiert (Vignettenländer, Streckenmaut,
   * mautfrei — je nach Name), und die Fläche gruppiert genau so. Der Browser
   * legt sie beim Laden einmal in eine Tabelle um, damit ein Land in O(1)
   * nachzuschlagen ist.
   */
  land: string;
  name: string;
  system: Mautsystem;
  jahresregel?: Jahresregel | null;
  /** Wer die Maut erhebt — ASFINAG, DARS, … */
  traeger?: string | null;
  optionen?: Mautoption[] | null;
  /** AT: die Strecken, die **nicht** in der Vignette stecken. */
  sondermaut?: Sondermautstrecke[] | null;
  /** Tunnel und Brücken in einem sonst mautfreien oder vignettenpflichtigen Land. */
  sonderbauwerke?: Sondermautstrecke[] | null;
  /** IDs der Zugänge, die hier gelten. */
  zugaenge?: string[] | null;
  hinweise?: string[] | null;
  /**
   * Was für Elektroautos gilt.
   *
   * `befreit` bei Tschechien — und ausdrücklich **erst nach der Anmeldung** im
   * Shop. Deshalb ist es ein Wort und kein `true`: Ein `ev: true` hätte in der
   * Fläche „nichts zu tun" bedeutet, und genau das stimmt nicht.
   */
  ev?: string | null;
  /** Wie bezahlt wird: `Mautstelle (Karte, Bargeld)`, `Kennzeichenerfassung`. */
  bezahlen?: string | null;
  /** Richtwert je Kilometer bei Streckenmaut, in Euro. */
  richtwert_eur_km?: number | null;
  /** Das Jahr, aus dem die Beträge stammen. */
  stand?: string | null;
}

/** Ein Mautzugang — Box, Kennzeichenerfassung oder Karte. */
export interface Mautzugangsart {
  /** Die Katalog-ID — sie steht in der Zeile, weil auch `zugaenge` eine Liste ist. */
  id: string;
  name: string;
  art: 'box' | 'kennzeichen' | 'karte';
  /** Länder, für die der Zugang gedacht ist. */
  laender: string[];
  /** Länder, die er zusätzlich mitdeckt (Telepass Europeo: FR, ES, PT). */
  europa?: string[] | null;
  /** `sondermaut`, wenn er nicht das Land, sondern nur dessen Sonderstrecken deckt. */
  deckt?: string | null;
  hinweis?: string | null;
}

/**
 * Der ganze Katalog.
 *
 * **Was das Web davon liest** (für die Abnahme gegen den echten Server):
 * je Land `land`, `name`, `system`, `jahresregel`, `hinweise`, `optionen`
 * (`key`, `name`, `tage`, `monate`, `jahr`, `eur`, `ca`, `original`) und
 * `sonderbauwerke` (`name`, `eur`); je Zugang `id`, `name`, `laender`,
 * `europa` und `hinweis`. Nicht ausgewertet werden `traeger`, `stand`, `ev`,
 * `bezahlen`, `richtwert_eur_km`, `sondermaut` und `tunnel`: Die stehen an der
 * **Landzeile** des Reise-Endpunkts, wo auch der Zustand hängt, und werden
 * dort gelesen. Sie bleiben im Typ, weil sie in der Antwort stehen und dieser
 * Typ deren Form beschreibt.
 */
export interface Mautkatalog {
  /** Sortiert nach System und Name — die Reihenfolge der Auswahl im Dialog. */
  laender: Mautland[];
  zugaenge: Mautzugangsart[];
  /**
   * Tunnel zwischen zwei Ländern (Mont-Blanc, Fréjus).
   *
   * Sie gehören keinem Land allein und stehen deshalb getrennt; an den
   * betroffenen Ländern tauchen sie zusätzlich unter `sonderbauwerke` auf. Die
   * Fläche liest sie dort und braucht diese Liste nicht — sie steht im Typ,
   * weil sie in der Antwort steht.
   */
  tunnel?: Sondermautstrecke[];
  /** Der Stand des ganzen Katalogs: `2025/2026`. */
  stand?: string | null;
  /** Der Satz über Anhaltswerte und Umrechnung, wörtlich für die Fläche. */
  hinweis?: string | null;
}

/** Was `GET /mautkatalog/gueltigkeit` zurückgibt — die Vorschau aufs Ende. */
export interface Mautgueltigkeit {
  land: string;
  option: string;
  name?: string | null;
  eur?: number | null;
  gueltig_von: string | null;
  /** `null`, wo sich das Ende nicht rechnen lässt — dann trägt es ein Mensch ein. */
  gueltig_bis: string | null;
  jahresregel?: Jahresregel | null;
}

/**
 * Was für ein Posten das ist.
 *
 * `jahr` und `zeitraum` **decken** ein Land in ihrem Fenster; `digital` ist ein
 * hinterlegtes Zahlungsmittel (AT-Flex-Maut, Telepass, Bip&Go) und deckt
 * **nie** — die Maut kostet trotzdem, sie wird nur anders bezahlt.
 */
export type Vignettenart = 'jahr' | 'zeitraum' | 'digital';

/** Ein Posten, wie er am Fahrzeug liegt. */
export interface Fahrzeugvignette {
  /**
   * 8 Hex, **vom Server** — der Griff für Ändern und Löschen.
   *
   * Ein frisch angelegter Posten geht ohne hinaus (`null`), und der Server
   * vergibt sie beim Speichern. Eine im Browser erfundene Kennung wäre eine
   * zweite Quelle für etwas, das stabil sein muss.
   */
  id?: string | null;
  /**
   * ISO-2, groß — nicht auf die Vignettenländer eingeschränkt.
   *
   * Darf **fehlen**, wenn ein Zugang gewählt ist: Welche Länder ein Telepass
   * deckt, steht im Katalog und nicht am Posten. Es dort zu wiederholen wäre
   * eine Kopie, die veraltet, sobald Telepass ein Land dazunimmt.
   */
  land?: string | null;
  art: Vignettenart;
  /**
   * Der Katalogschlüssel der gewählten Option (`10tage`, `jahr`, …).
   *
   * Nur bei `jahr`/`zeitraum`. Mit ihm rechnet der Server das Ende aus dem
   * Beginn — die Datumsrechnung (Tage, Monate, Dezember-Regel, rollendes Jahr)
   * liegt im Katalog und nirgends sonst. Fehlt die Option, gilt der
   * Sonstige-Zeitraum-Fall: dann zählen die beiden Daten.
   */
  option?: string | null;
  /** Katalog-ID des Zugangs (`telepass`, `asfinag-flex`, …). Nur bei `digital`. */
  zugang?: string | null;
  /** `JJJJ-MM-TT`. Bei `digital` darf beides fehlen, mit `option` das Ende. */
  gueltig_von: string | null;
  gueltig_bis: string | null;
  notiz: string | null;
}

/**
 * Der abgeleitete Stand eines berührten Landes.
 *
 * Die ersten vier gehören zu `system: 'vignette'`, die beiden mittleren zu
 * `system: 'strecke'`, `frei` zu einem mautfreien Land. Getrennte Wörter und
 * nicht ein gemeinsames „in Ordnung": `mautstelle` heißt, dass an der
 * Mautstelle gezahlt wird (kein Handlungsbedarf, aber auch keine Entwarnung
 * über die Kosten), `befreit` heißt, dass dieses Auto gar nichts zahlt — bei
 * Tschechien allerdings erst nach einer Anmeldung.
 */
export type Vignettenstandwort =
  | 'gedeckt'
  | 'ablaeuft'
  | 'offen'
  | 'befreit'
  | 'zugang'
  | 'mautstelle'
  | 'frei';

/**
 * Woher das Grün kommt.
 *
 * `fahrzeug` ist ein Posten mit Datum, `reise` ein Bestandshaken aus
 * `settings.vignetten` — der trägt kein Datum und kann deshalb nicht ablaufen.
 * Der Unterschied steht in der Fläche, weil er die ganze Aussage trägt.
 * `katalog` heißt: Es gibt nichts zu hinterlegen, weil dieses Auto befreit ist
 * (Tschechien, Elektro).
 */
export type Vignettenquelle = 'fahrzeug' | 'reise' | 'katalog';

/**
 * Was der Server für den Knopf vorschlägt — die Landesregel, einmal im Haus.
 *
 * Seit dem Katalog ist das die **günstigste Option, die die Reisetage deckt**
 * (§ 13.4), und nicht mehr pauschal die Jahresvignette: Bei zwölf Tagen in
 * Österreich ist die 2-Monats-Vignette billiger als das Jahr und teurer als
 * die zehn Tage, die nicht reichen. Diese Rechnung gehört dem Server.
 */
export interface Vignettenvorbelegung {
  art: Vignettenart;
  /** Katalogschlüssel der vorgeschlagenen Option. */
  option?: string | null;
  /** Ihr Name und Preis — damit die Fläche den Vorschlag benennen kann. */
  name?: string | null;
  eur?: number | null;
  gueltig_von: string | null;
  /**
   * `null`, wo sich das Ende nicht rechnen lässt.
   *
   * Der Fall ist die bulgarische Wochenendvignette: Sie hängt am Wochenende
   * und nicht am Kauftag. Dann trägt es ein Mensch ein — geraten wird es nicht.
   */
  gueltig_bis: string | null;
}

export interface Vignettenland {
  land: string;
  /** Der Landesname aus dem Katalog — die Fläche braucht ihn nicht zu suchen. */
  name?: string | null;
  /**
   * Welches Mautsystem das Land hat.
   *
   * Optional, weil ein Server aus der Zeit vor dem Katalog es nicht schickt —
   * dort waren alle Zeilen Vignettenländer, und `vignette` ist deshalb die
   * Annahme, wenn das Feld fehlt.
   */
  system?: Mautsystem | null;
  stand: Vignettenstandwort;
  quelle: Vignettenquelle | null;
  /** Das Ende des deckenden Postens, wenn es eines gibt. */
  gueltig_bis: string | null;
  /**
   * Anhaltswert für dieses Land, damit die Zeile keine zweite Preisliste braucht.
   *
   * Bei `system: 'vignette'` der Preis der günstigsten deckenden Option, bei
   * `strecke` nichts (`null`) — eine Streckenmaut hat keinen Preis, sondern
   * einen Kilometersatz.
   */
  eur: number | null;
  /**
   * Optional: Ein Server aus der Zeit vor der Dezember-Regel schickt sie
   * nicht, und dann belegt `vignetten.vorbelegung()` ersatzweise vor.
   */
  vorbelegung?: Vignettenvorbelegung | null;
  /**
   * Die Optionen dieses Landes — aus dem Katalog, schon mitgeschickt.
   *
   * Der Dialog nimmt sie von hier, wenn er über den Knopf an einer Zeile
   * geöffnet wird: Dann kennt er die Auswahl, ohne auf den Katalog zu warten.
   * Über den Abschnittskopf (ohne Landzeile) tritt `GET /mautkatalog` ein.
   */
  optionen?: Mautoption[] | null;
  // `posten_id` (die Kennung des deckenden Postens) schickt der Server mit; die
  // Fläche liest sie **nicht**: Geändert wird im Blatt, und dort liegt die
  // ganze Liste. Ein Feld im Typ ohne Leser täuscht eine Fähigkeit vor —
  // dieselbe Begründung wie bei `preferred_operators` am Fahrzeug.
  /** Bei Streckenmaut: die Zugänge, die hier gelten, mit Namen. */
  zugaenge?: { id: string; name: string | null }[] | null;
  /** Bei Streckenmaut: wie bezahlt wird, und der Richtwert je Kilometer. */
  bezahlen?: string | null;
  richtwert_eur_km?: number | null;
  /** Tunnel und Brücken dieses Landes — Hinweis, nie Handlungsbedarf. */
  sonderbauwerke?: Sondermautstrecke[] | null;
  /**
   * Sätze aus dem Katalog, die an die Zeile gehören.
   *
   * Hier stehen die Fristen, an denen es wirklich teuer wird: Free-Flow in
   * Frankreich will binnen 72 Stunden online bezahlt werden, die englische
   * Dartford Crossing binnen 24. Eine Zeile ohne diesen Satz sagt „an der
   * Mautstelle zahlen" und verschweigt, dass es dort keine gibt.
   */
  hinweise?: string[] | null;
  /**
   * Der Zugang, der dieses Land bezahlt — wenn einer am Fahrzeug liegt.
   *
   * `id: null` bei einem Posten, der nur ein Land nennt und keinen
   * Katalogzugang: Auch der zählt, denn was er bezahlt, weiß der Halter.
   */
  zugang?: {
    id: string | null;
    name: string | null;
    notiz?: string | null;
    gueltig_bis?: string | null;
    hinweis?: string | null;
  } | null;
  /** Nur AT: die Sondermautstrecken und ob ein Zugang sie abrechnet. */
  sondermaut?: {
    strecken: Sondermautstrecke[];
    stand: 'zugang' | 'mautstelle';
    zugang?: { id: string | null; name: string | null } | null;
  } | null;
}

/**
 * Ein hinterlegtes Zahlungsmittel, je Land eine Zeile.
 *
 * Ein Telepass Europeo liegt **einmal** am Fahrzeug und steht hier viermal —
 * er gilt in vier Ländern, und die Fläche soll ihn in jedem zeigen, ohne den
 * Katalog zu befragen. Senkt keine Schätzung und deckt kein Land.
 */
export interface Mautzugang {
  land: string;
  /** Katalog-ID, oder `null` bei einem Posten, der nur ein Land nennt. */
  zugang: string | null;
  name: string | null;
  notiz: string | null;
  gueltig_bis: string | null;
  /** `strecke` oder `sondermaut` — ASFINAG FLEX bezahlt nur die zweite. */
  deckt?: string | null;
}

export interface Vignettenstand {
  /** `null`, wenn das Konto kein Fahrzeug hat — dann gibt es nichts zu hinterlegen. */
  fahrzeug_id: number | null;
  zeitraum: { von: string | null; bis: string | null } | null;
  /**
   * Ohne `begin_date` ist keine Gültigkeit prüfbar.
   *
   * Dann stehen alle Pflichtländer auf `offen`, und die Fläche sagt den Grund.
   * Geraten wird nichts — ein angenommener Reisebeginn machte aus einer
   * ungeprüften Reise eine gedeckte.
   */
  zeitraum_fehlt: boolean;
  /**
   * Reisetage, beide Enden einschließlich — oder `null` ohne Zeitraum.
   *
   * Die Zahl, aus der die Vorbelegung ihre Laufzeit wählt. `null` heißt, dass
   * der Server mit einem Ersatzwert gerechnet hat; die Fläche sagt das, statt
   * einen Vorschlag als Maßarbeit auszugeben.
   */
  tage: number | null;
  laender: Vignettenland[];
  maut_zugang: Mautzugang[];
  /** Die geprüften Posten, wie gespeichert — die Grundlage des Blattes. */
  am_fahrzeug: Fahrzeugvignette[];
}

/* ══ Ladekarten, Betreiberkatalog, Reiseprofile ══════════════════════
 *
 * Die Formen aus § 1.2, 1.3 und 2.1 des Vertrags — und zwar **nur die Form
 * über den Draht**. Gerechnet wird mit ihnen im Kern
 * (`planner/ladekarten.ts`), und der führt seine eigenen, deckungsgleichen
 * Typen: Er läuft über `kern.ts` auch auf dem iPhone und darf `api.ts` nicht
 * kennen. Zwei Deklarationen derselben Form sind hier kein Versehen, sondern
 * die Grenze zwischen Anwendung und Kern — dass sie zusammenpassen, hält
 * `tests/ladekarten.test.mjs` an derselben Fixture fest, die auch der
 * Python-Spiegel liest (`tests/fixtures/ladekarten.json`).
 */

/**
 * Eine Preisregel einer Ladekarte.
 *
 * ## Was `eur_kwh: null` bedeutet
 *
 * **Preis unbekannt**, nicht kostenlos — und eine solche Regel greift nicht.
 * Das ist der Normalfall beim Roaming: Was eine fremde Säule über den eigenen
 * Vertrag kostet, steht in keinem Preisblatt, und einen Betrag zu erfinden
 * wäre die schlechtere Antwort als der ehrliche Rückfall auf den
 * Ad-hoc-Richtwert. Die Oberfläche setzt an solche Zeilen die Marke
 * „Preis fehlt“ (siehe `lib/ladekarten.ts`).
 *
 * ## Vorrang
 *
 * Betreiberliste vor `eigen` vor `roaming`; innerhalb einer Stufe Land vor
 * `*` und Art vor `*`. Die Reihenfolge in der Tabelle sagt darüber nichts —
 * sie ist die Reihenfolge, in der jemand die Zeilen eingetippt hat.
 */
export interface Ladekartenregel {
  /** `eigen`, `roaming` oder eine Liste von Katalog-IDs. */
  gilt: 'eigen' | 'roaming' | string[];
  /** `*` oder ISO-2 in Großbuchstaben. */
  land?: string | null;
  art?: 'AC' | 'DC' | '*' | null;
  /** €/kWh — oder `null` für „Preis fehlt“ (siehe Kopf). */
  eur_kwh: number | null;
  /** Sitzungsgebühr, einmal je Ladevorgang. */
  start_eur?: number | null;
  /** Blockiergebühr je Minute, ab `blockier_ab_min`, gedeckelt. */
  blockier_eur_min?: number | null;
  blockier_ab_min?: number | null;
  blockier_max_eur?: number | null;
  /** Richtwert, nicht bestätigt — wandert als Marke an den Ladestopp. */
  ca?: boolean | null;
}

/**
 * Eine Ladekarte, wie sie in `users.ladekarten` steht.
 *
 * Zwei Karten **gleichen Namens** sind zwei Preisstände derselben Karte (so
 * liegen die IONITY-Sätze vor und nach dem 13.10.2026 vor); gerechnet wird
 * der jüngste, der am Reisetag schon galt. Dass daraus keine zwei Karten
 * werden, entscheidet allein der Name — deshalb sagt die Oberfläche es an der
 * Zeile (`ladekarten.staendeJeName`).
 */
export interface Ladekarte {
  /** 8 Hex, vom Server vergeben. Fehlt sie, war die Karte noch nie gespeichert. */
  id?: string | null;
  name: string;
  /** Katalog-ID des Anbieters, oder `null` bei einer fremden Karte. */
  anbieter?: string | null;
  /** 1 = liebste Karte. Die Reihenfolge in der Liste ist der Rang. */
  rang?: number | null;
  grundgebuehr_eur?: number | null;
  je?: 'monat' | 'jahr' | null;
  /** Grundgebühr in die Reiseprognose umlegen? Vorgabe: nein. */
  umlegen?: boolean | null;
  gueltig_ab?: string | null;
  gueltig_bis?: string | null;
  notiz?: string | null;
  /** `uebernommen`, wenn die Karte aus den alten Fahrzeugtarifen entstand. */
  quelle?: string | null;
  regeln?: Ladekartenregel[] | null;
}

/** Ein Betreiber des Katalogs (§ 2.1). Pflegeliste, nicht Wahrheit. */
export interface Katalogbetreiber {
  id: string;
  name: string;
  /**
   * Schreibweisen, unter denen dieser Betreiber in den Säulendaten auftaucht.
   *
   * Sie sind der Grund, warum es den Katalog überhaupt gibt: Open Charge Map,
   * GoingElectric und OpenStreetMap schreiben „EnBW“, „EnBW mobility+“ und
   * „HyperNetz“ für dasselbe Netz, und ohne diese Liste zahlt der Plan an
   * zwei von drei Säulen den Ad-hoc-Preis, obwohl eine Karte gilt.
   */
  aliase?: string[] | null;
  /** Länder, in denen der Betreiber vorkommt. Nur zur Anzeige. */
  laender?: string[] | null;
}

/**
 * Die Antwort auf `GET /ladekatalog`.
 *
 * Drei Dinge in einem Abruf, weil sie zusammen gebraucht werden: die
 * Betreiberliste (Suche im Kartenblatt **und** Zuordnung im Kern), die
 * Ad-hoc-Richtwerte je Land (der Preis ohne Vertrag) und die Kartenvorlagen.
 * Der Abruf hängt an keinem Konto und ändert sich einmal im Jahr — `stand`
 * sagt, von wann die Preise sind, und die Seite schreibt es hin.
 */
export interface Ladekatalog {
  stand?: string | null;
  betreiber: Katalogbetreiber[];
  /** DC-Richtwerte je Land in €/kWh, dazu `*` als Rückfall. Alles `ca`. */
  adhoc?: Record<string, number> | null;
  /** Die Vorlagen aus § 2.4 — fertige Karten zum Übernehmen. */
  vorlagen?: Ladekarte[] | null;
}

/** Quartier-Vorgaben eines Reiseprofils (§ 1.3). */
export interface Profilquartier {
  arten?: string[] | null;
  vorlieben?: string[] | null;
  hoechstpreis_eur?: number | null;
}

/**
 * Ladevorgaben eines Reiseprofils — fünf Zahlen, zwei Leser.
 *
 * `soc_min`, `soc_max` und `ankunft_soc` liest der Kern (er überschreibt
 * damit die Fahrzeugwerte); `min_kw` und `umweg_max_km` gehen an die
 * Säulenabfrage der Oberfläche. Sie stehen in **einem** Objekt, weil sie
 * zusammen eine Aussage über die Fahrweise sind — und weil der Kern das
 * Objekt unverändert durchreicht (siehe `planner/ladeplaner.Ladevorgaben`).
 */
export interface Profilladen {
  soc_min?: number | null;
  soc_max?: number | null;
  min_kw?: number | null;
  umweg_max_km?: number | null;
  ankunft_soc?: number | null;
}

/** Kajak-Vorgaben eines Profils. Gelesen von `reise_zeigen`, angezeigt im Web. */
export interface Profilkajak {
  tempo_kmh?: number | null;
  tages_km?: number | null;
}

/** Was Lotte unterwegs anders macht. Gelesen im dynamischen Prompt-Teil. */
export interface Profillotte {
  erzaehltiefe?: 'kurz' | 'ausfuehrlich' | null;
  /** `false` schaltet die Vertiefungsfrage ab. */
  vertiefung?: boolean | null;
}

/**
 * Ein Reiseprofil (§ 1.3) — die Vorgaben, mit denen eine neue Reise anfängt.
 *
 * Alle Felder außer `id` und `name` sind optional, und das ist die tragende
 * Eigenschaft: Beim Anwenden lässt ein `null`-Feld den Reisewert in Ruhe
 * (`reiseprofile.anwenden` auf dem Server). Ein Profil ist damit ein **Satz
 * von Aussagen** und keine vollständige Reisebeschreibung — „09:00 bis 22:00,
 * ohne Grenzen“ sagt nichts über das Budget, und genau das soll es auch
 * nicht.
 */
export interface Reiseprofil {
  id: string;
  name: string;
  beschreibung?: string | null;
  /** `HH:MM` lokal. */
  tagesstart?: string | null;
  tagesende?: string | null;
  max_fahrzeit_std?: number | null;
  max_strecke_km?: number | null;
  antrieb?: 'elektro' | 'verbrenner' | null;
  uebernachtung?: 'fahrzeug' | 'unterkunft' | 'gemischt' | null;
  quartier?: Profilquartier | null;
  laden?: Profilladen | null;
  /** Freie Merkmale (klein, ASCII, ≤ 20 Zeichen) — sie schalten Packposten. */
  merkmale?: string[] | null;
  kajak?: Profilkajak | null;
  lotte?: Profillotte | null;
  budget_tag_eur?: number | null;
  /**
   * `vorlage`, wenn das Profil vom Server mitgebracht wurde.
   *
   * Ein Feld und kein Vergleich gegen eine Namensliste im Browser: Welche
   * Profile Vorlagen sind, weiß der Server, und eine zweite Meinung darüber
   * setzte die Marke „Vorlage“ irgendwann an ein eigenes Profil.
   */
  quelle?: string | null;
}

/**
 * Der Herkunftsvermerk an der Reise — `settings.profil`.
 *
 * Er sagt, **welches** Profil wann angewandt wurde, und ist damit die
 * Grundlage der Zeile in den Reiseeinstellungen. Der Name steht mit drin und
 * nicht nur die Kennung: Ein gelöschtes Profil soll die Zeile nicht zu
 * „Profil: p3“ machen.
 */
export interface Profilvermerk {
  id?: string | null;
  name?: string | null;
  /** Zeitpunkt des Anwendens, als ISO-Zeichenkette. */
  stand?: string | null;
}

export const api = {
  health: () => request<Health>('/health'),
  me: () => request<{ user: User }>('/auth/me'),
  /**
   * Profileinstellungen ändern — Anzeigedichte und Standard-Packliste.
   *
   * Eigener Aufruf und kein Feld an der Reise: Beides gilt für alle Reisen
   * dieses Kontos. Die Antwort trägt den ganzen Nutzer, damit der Aufrufer den
   * Stand vom Server übernimmt statt seinen eigenen zu glauben.
   *
   * `packliste: null` ist kein Versehen, sondern die **Rückkehr zur
   * eingebauten Vorgabe**: Die eigene Liste wird weggeräumt, und der Server
   * antwortet danach mit `quelle: 'vorgabe'`. Ein leeres `{ posten: [] }`
   * wäre etwas anderes, nämlich eine ausdrücklich leere eigene Liste — und
   * genau die will man nach einem Fehlgriff im Editor nicht geschenkt
   * bekommen.
   */
  patchMe: (data: {
    expertenansicht?: boolean;
    packliste?: { posten: Packposten[] } | null;
    /*
     * Die drei Kontofelder des Grundlagen-Pakets. Für jedes gilt dieselbe
     * Lesart wie bei `packliste`: **nicht mitgeschickt** heißt „lass es in
     * Ruhe", ausdrückliches `null` heißt „räum es weg".
     *
     * Dasselbe gilt für `ladekarten` und `reiseprofile` — mit einem
     * Unterschied im Sinn von `null`: Bei den Karten heißt es „räum sie weg,
     * und übernimm beim nächsten Lesen wieder die alten Fahrzeugtarife“, bei
     * den Profilen „zurück zu den Vorlagen des Servers“. Beides ist eine
     * Handlung, die jemand ausdrücklich auslöst, und beide haben deshalb eine
     * Rückfrage vor sich.
     */
    zuhause?: Zuhause | null;
    vertrauenspersonen?: Vertrauensperson[] | null;
    lotte_notiz?: string | null;
    /**
     * Die Ladekarten, vollständig und in Rangfolge.
     *
     * **Immer die ganze Liste** und kein Einzelposten: Der Rang ist die
     * Reihenfolge, und eine Karte einzeln zu schicken hieße, dem Server zwei
     * Meinungen über diese Reihenfolge zu geben. Der Prüfer vergibt die Ränge
     * lückenlos neu und antwortet bei einer unbekannten Katalog-ID mit 422 —
     * was er beanstandet, steht als Satz in der Antwort.
     */
    ladekarten?: Ladekarte[] | null;
    /** Die Reiseprofile, vollständig. `null` heißt: zurück zu den Vorlagen. */
    reiseprofile?: Reiseprofil[] | null;
  }) => request<{ user: User }>('/auth/me', { method: 'PATCH', body: JSON.stringify(data) }),
  /**
   * Der Medienplatz, aufgeschlüsselt.
   *
   * Ein 404 heißt: Dieser Server kennt den Endpunkt noch nicht. Die
   * Kontoseite fällt dann auf `medien_belegt_mb`/`medien_limit_mb` aus
   * `/auth/me` zurück und sagt, dass die Aufschlüsselung fehlt — eine leere
   * Fläche sähe aus wie „nichts belegt".
   */
  speicher: () => request<Speicherstand>('/auth/me/speicher'),
  /**
   * Der Betreiberkatalog — Betreiber, Ad-hoc-Richtwerte, Kartenvorlagen.
   *
   * Ohne Konto und ohne Reise: Er beschreibt Europa und nicht den Nutzer.
   * Ein 404 heißt, dass dieser Server ihn noch nicht kennt — dann rechnet der
   * Kern den heutigen Weg (Betreiber über Namensgleichheit, ad hoc flach
   * 0,59), und das Kartenblatt bietet keine Anbietersuche und keine Vorlagen
   * an. Das ist der Rückfall, auf den `Planner.svelte` und
   * `LadekartenSeite.svelte` ausdrücklich vorbereitet sind: Beide behandeln
   * den fehlenden Katalog als Auskunft und nicht als Fehler.
   */
  ladekatalog: () => request<Ladekatalog>('/ladekatalog'),
  logout: () => request<{ status: string }>('/auth/logout', { method: 'POST' }),

  trips: () => request<Trip[]>('/trips'),
  trip: (id: number) => request<Trip>(`/trips/${id}`),
  /**
   * Eine Reise anlegen — mit Reiseart, und seit dem 12.9.2026 mit Profil.
   *
   * `profil_id` und `von_zuhause` sind **keine** Spalten der Reise, sondern
   * Anweisungen ans Anlegen: Das Profil wird beim Anlegen angewandt (der
   * Server ist die Wahrheit über `reiseprofile.anwenden`, siehe Vertrag
   * § 1.3), und `von_zuhause` setzt erste und letzte Station auf die Adresse
   * aus `users.zuhause`. Beides hier und nicht in zwei Nachträgen: Ein
   * zweiter Schreibvorgang kann scheitern, und dann stünde eine Reise da, die
   * anders ist als gerade ausgewählt.
   *
   * Ein Server, der die Felder nicht kennt, ignoriert sie — die Reise
   * entsteht dann ohne Profil, und die Oberfläche sagt es
   * (`reiseprofile.anlegemeldung`).
   */
  createTrip: (data: Partial<Trip> & { profil_id?: string | null; von_zuhause?: boolean }) =>
    request<Trip>('/trips', { method: 'POST', body: JSON.stringify(data) }),
  /**
   * Ein Profil auf eine **bestehende** Reise anwenden — „erneut anwenden“.
   *
   * Stationen, Haken und Buchungen bleiben unberührt (so steht es im Vertrag
   * § 1.3, und der Server hält es ein): Angewandt werden die Vorgaben, nicht
   * der Inhalt. Zurück kommt die frische Reise mit neuem `rev` und neuem
   * `settings.profil` — der Aufrufer übernimmt sie, statt seinen eigenen
   * Stand weiterzuglauben.
   */
  profilAnwenden: (tripId: number, profilId: string) =>
    request<Trip>(`/trips/${tripId}/profil/${encodeURIComponent(profilId)}`, {
      method: 'POST'
    }),
  /** `rev` aktiviert optimistisches Sperren: 409, wenn anderswo geschrieben wurde. */
  patchTrip: (id: number, data: TripPatch, rev?: number) =>
    request<Trip>(`/trips/${id}`, {
      method: 'PATCH',
      headers: rev === undefined ? undefined : { 'If-Match': String(rev) },
      body: JSON.stringify(data)
    }),
  deleteTrip: (id: number) => request<{ status: string }>(`/trips/${id}`, { method: 'DELETE' }),
  /**
   * Eine Reise kopieren — mit oder ohne Kopier-Abfrage.
   *
   * Ohne `optionen` geht der Aufruf **ohne Body** hinaus, also genau so, wie
   * er vor dem Dialog aussah. Das ist kein Sparen an einer Zeile: Der Server
   * nimmt den Body freiwillig entgegen, und der leere Aufruf ist der einzige,
   * bei dem es auf beiden Seiten nichts zu verwechseln gibt. Wann er
   * entsteht, entscheidet `kopierbody` — dort steht auch, warum dann alle vier
   * Felder mitgehen und nicht nur das abweichende.
   *
   * Zurück kommt die neue Reise, und **nur** bei `belege: true` zusätzlich
   * `belege_kopiert` und `belege_uebersprungen`. Deshalb `Kopie` und nicht
   * `Trip`: Die zwei Zahlen hängen flach an der Reise, ein `{ trip: … }` hätte
   * jeden heutigen Aufrufer gebrochen.
   */
  duplicateTrip: (id: number, optionen?: Kopieroptionen) =>
    request<Kopie>(`/trips/${id}/duplicate`, {
      method: 'POST',
      body: optionen === undefined ? undefined : JSON.stringify(optionen)
    }),
  /**
   * Die Beispielreise ins eigene Konto kopieren.
   *
   * Dieselbe Kopie, die eine Einladung mitbringt — der Server kennt die
   * Vorlage selbst (`BEISPIELREISE_TRIP_ID`), damit es **eine** Wahrheit
   * darüber gibt, welche Reise das ist. Hier geht deshalb kein Parameter
   * hinaus: Eine Kennung aus dem Browser wäre die zweite.
   *
   * Zwei Antworten sind keine Störung und werden in `onboarding.ts` zu einem
   * ruhigen Satz: **409**, wenn die Kopie schon liegt (es gibt sie einmal je
   * Konto), und **404**, wenn auf diesem Server keine hinterlegt ist.
   */
  beispielreise: () => request<Trip>('/trips/beispiel', { method: 'POST' }),
  deletedTrips: () => request<Trip[]>('/trips/deleted/list'),
  restoreTrip: (id: number) => request<Trip>(`/trips/${id}/restore`, { method: 'POST' }),
  /** Endgültig löschen. Geht serverseitig nur aus dem Papierkorb heraus. */
  purgeTrip: (id: number) =>
    request<{ ok: boolean }>(`/trips/${id}/purge`, { method: 'DELETE' }),

  /** Ersetzt die Stationsliste vollständig — siehe Kommentar in main.py. */
  /**
   * Stationen schreiben — mit optimistischer Sperre, wenn `rev` mitkommt.
   *
   * Der Aufruf **ersetzt** die Liste vollständig. Ohne Sperre überschreibt ein
   * zweites Fenster (oder das Telefon) alles, was seit dem Laden dazugekommen
   * ist, und zwar lautlos. Zurück kommt die neue Revision aus dem `ETag`.
   *
   * `anhaenge` geht **nicht** mit. Die Belege sind Verweise auf Dateien, und
   * der Server nimmt sie über diesen Weg bewusst nicht entgegen; sie hier
   * auszusieben ist die Zusicherung dazu (siehe `lib/anhaenge.ts`).
   */
  putStops: (tripId: number, stops: Stop[], rev?: number) =>
    requestMitKopf<Stop[]>(`/trips/${tripId}/stops`, {
      method: 'PUT',
      body: JSON.stringify(stops.map(ohneAnhaenge)),
      headers: rev === undefined ? undefined : { 'If-Match': String(rev) }
    }),

  /**
   * Ein PDF an die Buchung einer Station hängen — als **roher Body**.
   *
   * Kein `FormData`: Der Server nimmt die Bytes direkt entgegen, genau wie
   * beim Medien-Upload eine Zeile weiter unten, und der Anzeigename passt in
   * die Abfragezeichenkette. Ein Formular wäre auf beiden Seiten nur Umweg.
   *
   * Adressiert wird über `ord`, die Stelle der Station in der gespeicherten
   * Liste — nicht über `stops.id`, die kein `replace_stops` überlebt.
   *
   * Fehlschläge kommen mit lesbaren deutschen Sätzen zurück (400 für „kein
   * PDF“, „leer“, „zu groß“; 413 fürs Kontingent, mit beiden Mengen im Text).
   * Sie gehören unverändert in die Fläche — sie sind für Menschen geschrieben,
   * und eine eigene Übersetzung wäre eine zweite Wahrheit über dieselbe Regel.
   */
  anhangHochladen: (tripId: number, ord: number, datei: File) => {
    const p = new URLSearchParams({ name: datei.name });
    return request<AnhangAntwort>(`/trips/${tripId}/stops/${ord}/anhang?${p}`, {
      method: 'POST',
      body: datei,
      headers: { 'Content-Type': 'application/pdf' }
    });
  },

  /** Einen Beleg von der Station nehmen. Zurück kommt der Rest der Liste. */
  anhangEntfernen: (tripId: number, ord: number, anhangId: string) =>
    request<{ ok: boolean; ord: number; anhaenge: Anhang[] }>(
      `/trips/${tripId}/stops/${ord}/anhang/${anhangId}`,
      { method: 'DELETE' }
    ),

  /**
   * Einen Beleg holen und als Objekt-URL zurückgeben.
   *
   * ## Warum kein schlichtes `<a href="/anhang/…">`
   *
   * Weil diese Anwendung keine Sitzung im Cookie führt. Der Token liegt in
   * `localStorage` und geht als `Authorization`-Kopfzeile mit (siehe
   * `request`) — ein gewöhnlicher Anker schickt ihn nicht mit und bekäme
   * genau einen 401. Ihn dafür in die Adresse zu schreiben ist keine Option:
   * Dort landete er in den Zugriffsprotokollen und in der Browserchronik, und
   * diese Sitzung gilt ein Jahr. Dieselbe Abwägung wie bei `medienHolen`,
   * dieselbe Antwort — die Datei wird mit Kopfzeile geholt und als Blob
   * geöffnet.
   *
   * **Der Aufrufer muss die URL wieder freigeben** (`URL.revokeObjectURL`).
   */
  anhangHolen: async (anhangId: string): Promise<string> => {
    const token = getToken();
    const res = await fetch(`/api${anhangWeg(anhangId)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!res.ok) throw await fehlerAus(res);
    return URL.createObjectURL(await res.blob());
  },

  // ── Tagespläne ────────────────────────────────────────────────────
  //
  // Vorlagen ohne Reisebezug. Die Verbindung zur Reise steht an der Station
  // (`Stop.day_plans`), der Inhalt hier.
  plans: () => request<Tagesplan[]>('/plans'),
  plan: (id: number) => request<Tagesplan>(`/plans/${id}`),
  createPlan: (daten: Partial<Tagesplan>) =>
    request<Tagesplan>('/plans', { method: 'POST', body: JSON.stringify(daten) }),
  patchPlan: (id: number, daten: Partial<Tagesplan>) =>
    request<Tagesplan>(`/plans/${id}`, { method: 'PATCH', body: JSON.stringify(daten) }),
  deletePlan: (id: number) => request<{ status: string }>(`/plans/${id}`, { method: 'DELETE' }),
  /** Alle an dieser Reise hängenden Pläne, vollständig — ein Aufruf statt einer je Station. */
  tripPlans: (tripId: number) => request<Tagesplan[]>(`/trips/${tripId}/plans`),

  /**
   * Der Fußweg eines Stadttags, Abschnitt für Abschnitt.
   *
   * Höchstens zwölf Punkte je Aufruf — längere Ketten zerlegt `fussweg.ts` in
   * Blöcke mit geteiltem Nahtpunkt. Der Server **speichert nichts**: Das
   * Ergebnis gehört an den Einhänge-Eintrag der Station und wird von dort über
   * den gewöhnlichen stops-PUT geschrieben. Zwei Schreibwege auf dieselbe
   * Stelle wären zwei Orte, an denen sie auseinanderlaufen kann.
   */
  fussweg: (tripId: number, punkte: Ortspaar[]) =>
    request<Fusswegantwort>(`/trips/${tripId}/fussweg`, {
      method: 'POST',
      body: JSON.stringify({ punkte })
    }),

  /** Stationen frisch vom Server — nach einem Zusammenstoß der gültige Stand. */
  stops: (tripId: number) => request<Stop[]>(`/trips/${tripId}/stops`),

  legs: (tripId: number) => request<Leg[]>(`/trips/${tripId}/legs`),
  /**
   * Legt den gerechneten Plan als Abbild ab — für die iOS-App, für
   * `ladeplan_zeigen` und `tag_zeigen` über MCP und für die geteilte Seite.
   *
   * Der Schnappschuss kommt aus **einer** Hand: `planner/eingabe.planVoll()`.
   * Deshalb steht hier sein Typ und nicht mehr `Record<string, unknown>` — ein
   * von Hand zusammengesetztes Abbild wäre die zweite Fassung derselben
   * Feldnamen, und die Seite, auf der man den Unterschied sieht, ist ein
   * Gespräch mit Claude.
   *
   * `planner_version` gibt der Aufrufer mit, und er nimmt sie aus dem
   * Schnappschuss (`snapshot.version`) — nicht aus einer zweiten Konstante
   * hier: Zwei Stellen, die die Version nennen, sind eine Gelegenheit, sie
   * verschieden zu nennen. Die Spalte bleibt gefüllt, weil `plan_veraltet`
   * und `_ladeplan_altersatz` sie lesen.
   *
   * **`laden` ist dreiwertig** (Vertrag vom 11.9.2026, `db.save_plan_snapshot`):
   * fehlt der Schlüssel, behält der Server den vorhandenen Ladeplan; `null`
   * löscht ihn; ein Objekt ersetzt ihn. Entschieden wird das in `planVoll`,
   * und `JSON.stringify` trägt es unverändert herüber — ein `undefined` wäre
   * hier kein Ersatz für das Weglassen, aber genau das liefert `planVoll`
   * ohnehin nie.
   */
  savePlan: (tripId: number, snapshot: Schnappschuss, planner_version: string) =>
    request<{ status: string }>(`/trips/${tripId}/plan`, {
      method: 'PUT',
      body: JSON.stringify({ snapshot, planner_version })
    }),
  /** Berechnet die Etappen neu. Unveränderte kommen serverseitig aus dem Cache. */
  computeLegs: (tripId: number) =>
    request<LegResult>(`/trips/${tripId}/legs`, { method: 'POST' }),

  /* ── Verlässlichkeit vor Ort ────────────────────────────────────────
   *
   * Drei POSTs auf Zuruf und kein einziger Leseweg, der nebenbei prüft: Jeder
   * kostet Fremdanfragen (Photon und Overpass je Station, eine Websuche je
   * Ort und Tag), und bei 162 Stationen sind das Minuten und Geld. Was sie
   * ergeben, steht danach in der Reise — die Oberfläche liest es aus
   * `stops.ortsschaerfe` bzw. `trip.settings`, nicht aus dem Aufruf.
   */

  /**
   * Alle Stationen der Reise auf Ortsschärfe prüfen — **verändert Stationen**.
   *
   * Steht genau ein Parkplatz in 150 Metern, rastet der Server die Station
   * darauf ein: neue Koordinate, geleerte Adresse. Was es getroffen hat, steht
   * in `eingerastet`. Der Aufrufer muss danach zweierlei tun — die Stationen
   * neu laden (die Koordinaten im Browser sind sonst die alten) und die Route
   * neu rechnen (eine verschobene Station verschiebt ihre Etappe).
   *
   * Dauert bei einer großen Reise Minuten. Ohne Stationen antwortet der Server
   * mit 404 und einem lesbaren Satz statt mit einer leeren Liste.
   */
  ortsschaerfePruefen: (tripId: number) =>
    request<Ortsschaerfeantwort>(`/trips/${tripId}/ortsschaerfe`, { method: 'POST' }),

  /**
   * Der Fortschritt eines laufenden Ortsschärfe-Laufs.
   *
   * Auf Verdacht gebaut: Heute antwortet der POST oben noch synchron. Die
   * Fläche fragt diesen Weg deshalb nur ab, **nachdem** eine Antwort mit
   * `laeuft: true` kam — ein Server ohne diesen Weg wird also nie danach
   * gefragt und muss auch keinen 404 dafür liefern.
   */
  ortsschaerfeStand: (tripId: number) =>
    request<Ortsschaerfestand>(`/trips/${tripId}/ortsschaerfe/stand`),

  /**
   * „Lage stimmt so" an einer Station setzen oder zurücknehmen.
   *
   * Der schmale Schreibweg neben `putStops`: ein Knopf am Befund soll keine
   * ganze Stationsliste schicken. Adressiert wird über `ord`, die Stelle in
   * der gespeicherten Liste — nicht über `stops.id`, die kein `replace_stops`
   * überlebt.
   *
   * Die Zusage gilt für **diese Koordinate**; wandert die Station, fällt sie
   * serverseitig zurück auf `false`.
   */
  ortsschaerfeBestaetigen: (tripId: number, ord: number, bestaetigt = true) =>
    request<{ position: number; name: string | null; ortsschaerfe: Ortsschaerfe }>(
      `/trips/${tripId}/stops/${ord}/ortsschaerfe/bestaetigen?bestaetigt=${bestaetigt}`,
      { method: 'POST' }
    ),

  /**
   * Was ist an den Tagen dieser Reise an den Zielorten los?
   *
   * Ohne `datum` die ganze Reise; das Ergebnis ersetzt `settings.lage`. Mit
   * `datum` genau ein Tag, und dann landet es in `settings.lage_morgen`, dem
   * Zwischenspeicher des Vorabends. Wohin geschrieben wurde, sagt die Antwort
   * selbst (`gespeichert_als`) — der Aufrufer soll nicht raten müssen, welchen
   * Block er neu laden muss.
   *
   * Braucht einen gespeicherten Plan: Welche Station an welchem Kalendertag
   * steht, weiß nur der Schnappschuss (der Server hat keinen Scheduler). Ohne
   * ihn kommt 404 mit genau diesem Satz zurück.
   */
  lagePruefen: (tripId: number, datum?: string) =>
    request<Lageantwort>(
      `/trips/${tripId}/lage${datum ? `?datum=${encodeURIComponent(datum)}` : ''}`,
      { method: 'POST' }
    ),

  /**
   * Wie weit der Lage-Lauf ist — und was er bisher gefunden hat.
   *
   * **Der einzige Leseweg, der während eines Laufs etwas Neues zeigt.** Er
   * kostet nichts (keine Fremdanfrage, kein Geld) und darf deshalb im Takt
   * gefragt werden; wie oft und wie lange, entscheidet
   * `verlaesslichkeit.laufentscheid` — dort steht auch die Frist, an der die
   * Fläche aufhört zu fragen, statt bis zum Ende der Sitzung weiterzupollen.
   *
   * Antwortet auch, wenn gerade nichts läuft: dann mit `laeuft: false` und dem
   * zuletzt gespeicherten Stand. Ein Reiter, der frisch geöffnet wird, kann
   * also mit **einer** Frage klären, ob er sich an einen laufenden Lauf
   * anhängen muss.
   */
  lageStand: (tripId: number) => request<Lagestand>(`/trips/${tripId}/lage/stand`),

  /**
   * Die Alpenpässe der gerechneten Route recherchieren.
   *
   * Braucht eine gerechnete Route — ohne Etappen antwortet der Server mit 404
   * („Keine Route gerechnet"), und das ist die richtige Auskunft: „keine
   * Pässe" wäre eine Aussage über eine Strecke, die niemand kennt.
   *
   * Das Ergebnis ersetzt `settings.paesse`; gelesen wird es von dort
   * (`planner/vorbereitung.ts`), nicht aus dieser Antwort.
   */
  paessePruefen: (tripId: number) =>
    request<Passergebnis>(`/trips/${tripId}/paesse`, { method: 'POST' }),

  /* ── Befunde: lesen, ausblenden, sammelprüfen ──────────────────────
   *
   * Ein Leseweg, zwei Schreibwege und der Sammellauf. Der Unterschied
   * zwischen dem ersten und dem letzten ist der ganze Punkt: `befunde` liest
   * (kostet nichts, darf nach jeder Änderung gerufen werden), `reisePruefen`
   * prüft (kostet Fremdanfragen, Geld und Minuten und bewegt Stationen).
   */

  /**
   * Alle Befunde dieser Reise, sortiert nach Schwere — **ohne Seiteneffekt**.
   *
   * `{befunde, zaehlung, je_art, geprueft, stand}`. Daraus lebt der ganze
   * Vorbereitung-Reiter: die Liste oben, die Zahl im Panelkopf
   * (`zaehlung.offen`) und die Zähler der Abschnitte (`je_art`).
   *
   * Ein **404** heißt „dieser Server kennt die Befunde noch nicht" und ist
   * kein Fehlerzustand: Der Reiter zeigt dann den Stand von vorher, aus
   * Stationen und `trip.settings`. Dieselbe Unterscheidung wie bei der
   * Packliste und beim Mautstand.
   */
  befunde: (tripId: number) => request<Befundsicht>(`/trips/${tripId}/befunde`),

  /* ── Länder unterwegs ─────────────────────────────────────────────── */

  /**
   * Die Länder dieser Reise mit Landeswissen — Geld, Verkehr, Umgang, Notruf.
   *
   * Kostet nichts und darf nach jeder Änderung geholt werden: Der Server
   * liest die Stationen und schlägt im Katalog nach. Welches Land ab welchem
   * Tag gilt, steht als `grenztag` dabei; das Startland trägt `startland`.
   *
   * Ein **404** heißt „dieser Server kennt die Länder noch nicht" — die Karte
   * bleibt leer und sagt es, statt einen Fehler zu melden.
   */
  laender: (tripId: number) => request<Landsicht>(`/trips/${tripId}/laender`),

  /**
   * Der Länderkatalog ohne Reise — für die Währungsvorgabe am Kostenfeld.
   *
   * Hängt an keiner Reise und wird deshalb einmal geholt. Gebraucht wird
   * davon im Browser genau ein Feld: `waehrung.code` je Land. Der Katalog ist
   * die Wahrheit dieser Zuordnung; die Tabelle in `kosten.ts` ist nur die
   * Brücke für einen Server, der ihn noch nicht ausliefert.
   */
  laenderKatalog: () => request<Landkatalog>('/laender'),

  /**
   * Einen Befund ausblenden — „weiß ich, passt so".
   *
   * Gilt für **diesen** Befund und nicht für diese Stelle: Ändert sich die
   * Quelle (die Station wandert, der Passstatus wechselt), ändert sich die
   * Kennung, und der Befund erscheint neu. Das ist Absicht — ein Ausblenden
   * ist eine Antwort auf eine Auskunft und keine Dauervollmacht.
   *
   * Die Antwort trägt die frische Zählung mit; die Fläche holt trotzdem die
   * ganze Sicht nach, weil sich auch die Gruppen verschieben.
   */
  befundAusblenden: (tripId: number, befundId: string, notiz?: string) =>
    request<Befundzustand>(
      `/trips/${tripId}/befunde/${encodeURIComponent(befundId)}/ausblenden` +
        (notiz ? `?notiz=${encodeURIComponent(notiz)}` : ''),
      { method: 'POST' }
    ),

  /**
   * Einen ausgeblendeten Befund zurückholen.
   *
   * Die Gegenrichtung ist kein Beiwerk, sondern die Bedingung dafür, dass man
   * den ersten Knopf ohne Zögern drückt. `war_ausgeblendet: false` heißt „da
   * war nichts" und ist kein Fehler.
   */
  befundEinblenden: (tripId: number, befundId: string) =>
    request<Befundzustand>(
      `/trips/${tripId}/befunde/${encodeURIComponent(befundId)}/ausblenden`,
      { method: 'DELETE' }
    ),

  /**
   * „Reise prüfen" — Ortsschärfe, Lage und Pässe hintereinander, **am Server**.
   *
   * Seit dem 12.9.2026 steht die Reihenfolge dort und nicht mehr im Browser:
   * Die Ortsschärfe zuerst, weil sie Stationen bewegt, und die Lage-Kandidaten
   * hängen an ihnen. Sie stand vorher doppelt, und zwei Reihenfolgen derselben
   * Prüfung sind eine zu viel.
   *
   * Antwortet mit **202 und einer Quittung**, nicht mit dem Ergebnis: nginx
   * kappt einen POST nach 60 Sekunden, allein die Ortsschärfe braucht an einer
   * großen Reise Minuten. Den Fortschritt liefert `pruefStand`, das Ergebnis
   * `befunde`.
   *
   * **Kostet Geld und bewegt Stationen.** Ein zweiter Aufruf während eines
   * Laufs startet keinen zweiten, sondern liefert den Stand.
   */
  reisePruefen: (tripId: number) =>
    request<Pruefstand>(`/trips/${tripId}/pruefen`, { method: 'POST' }),

  /**
   * Wie weit der Sammellauf ist.
   *
   * Kostet nichts und darf im Takt gefragt werden — er liest die Buchführung
   * im Serverprozess und startet nie eine Prüfung. Antwortet auch, wenn nichts
   * läuft (`laeuft: false`); der Reiter klärt damit beim Öffnen mit **einer**
   * Frage, ob er sich an einen laufenden Lauf anhängen muss.
   */
  pruefStand: (tripId: number) => request<Pruefstand>(`/trips/${tripId}/pruefen/stand`),

  /* ── Packliste je Reise ────────────────────────────────────────────
   *
   * Vier Aufrufe, **eine** Antwortform. Das ist kein Zufall, sondern der
   * Grund, warum die Fläche so wenig Zustand braucht: Jeder Aufruf gibt die
   * ganze Liste samt `stand`, `quelle` und `bedingungen` zurück, und die
   * Fläche ersetzt ihren Zustand damit. Ein `PUT`, das nur `{ok: true}`
   * sagte, zwänge sie, ihren eigenen Stand zu glauben — und dann liefen
   * Browser und Server auseinander, sobald der Server etwas anders ablegt
   * (etwa `aktiv` nach einer geänderten Reise).
   */

  /**
   * Die Packliste dieser Reise.
   *
   * Antwortet auch ohne gespeicherte Liste mit Posten: Dann kommen sie aus dem
   * Konto oder aus der eingebauten Vorgabe, und `gespeichert` steht auf
   * `false`. Ein 404 heißt etwas anderes, nämlich dass dieser Server die
   * Packliste noch nicht kennt — die Fläche fällt dafür auf die eingebaute
   * Vorlage zurück, nur lesend (siehe `packliste.rueckfallposten`).
   */
  packliste: (tripId: number) => request<Packlistenstand>(`/trips/${tripId}/packliste`),
  /**
   * Die Liste schreiben — **vollständig**, wie bei den Stationen.
   *
   * Kein Teilpatch je Posten: Ein Haken, ein neuer Posten und ein gestrichener
   * Posten sind dieselbe Handlung an derselben Liste, und drei Endpunkte dafür
   * wären drei Gelegenheiten, die Reihenfolge zu verlieren.
   */
  packlisteSetzen: (tripId: number, posten: Packposten[]) =>
    request<Packlistenstand>(`/trips/${tripId}/packliste`, {
      method: 'PUT',
      body: JSON.stringify({ posten })
    }),
  /**
   * Aus der Standard-Packliste **ergänzen** — nicht ersetzen.
   *
   * Der Unterschied ist der ganze Sinn des Knopfs: Wer mitten in der
   * Vorbereitung merkt, dass er am Konto einen Posten nachgetragen hat, will
   * ihn hier dazu haben — und nicht seine zwölf Haken verlieren.
   */
  packlisteAusStandard: (tripId: number) =>
    request<Packlistenstand>(`/trips/${tripId}/packliste/aus-standard`, { method: 'POST' }),
  /**
   * Diese Liste als Standard für künftige Reisen festhalten.
   *
   * Überschreibt die Liste am Konto, deshalb steht in der Fläche eine
   * Rückfrage davor. Die Haken gehen dabei nicht mit — ein Standard, der
   * abgehakt anfängt, wäre auf der nächsten Reise eine Liste, die man nicht
   * mehr durchgeht.
   */
  packlisteAlsStandard: (tripId: number) =>
    request<Packlistenstand>(`/trips/${tripId}/packliste/als-standard`, { method: 'POST' }),

  poiKorridor: (tripId: number, cat: string, sub: string[], spread: number) =>
    request<Poi[]>(
      `/poi?trip=${tripId}&cat=${cat}&sub=${sub.join(',')}&spread=${spread}&limit=60`
    ),
  poiUmkreis: (mitte: { lat: number; lon: number }, cat: string, sub: string[], radius: number) =>
    request<Poi[]>(
      `/poi?lat=${mitte.lat}&lon=${mitte.lon}&cat=${cat}&sub=${sub.join(',')}&radius=${radius}&limit=60`
    ),

  geocode: (q: string, center?: { lat: number; lon: number }) => {
    const p = new URLSearchParams({ q });
    if (center) {
      p.set('lat', String(center.lat));
      p.set('lon', String(center.lon));
    }
    return request<GeoHit[]>(`/geocode?${p}`);
  },
  reverse: (lat: number, lon: number) =>
    request<GeoHit & { address: string }>(`/reverse?lat=${lat}&lon=${lon}`),

  vehicles: () => request<Vehicle[]>('/vehicles'),
  createVehicle: (v: Partial<Vehicle>) =>
    request<Vehicle>('/vehicles', { method: 'POST', body: JSON.stringify(v) }),
  updateVehicle: (id: number, v: Partial<Vehicle>) =>
    request<Vehicle>(`/vehicles/${id}`, { method: 'PUT', body: JSON.stringify(v) }),

  /* ── Vignetten am Fahrzeug ─────────────────────────────────────────
   *
   * Drei Aufrufe, zwei Schreibwege, und der Unterschied zwischen ihnen ist
   * kein Zufall:
   *
   * `fahrzeugVignetten` schreibt die **ganze** Liste (aus dem Blatt, wo sie
   * vollständig auf dem Tisch liegt). `vignetteHinterlegen` hängt **einen**
   * Posten an — für den Knopf im Vorbereitungsreiter, der nur den abgeleiteten
   * Stand kennt und nicht die Fahrzeugliste. Ein Lesen-Ändern-Schreiben von
   * dort verlöre einen Posten, den ein zweiter Tab gerade angelegt hat.
   *
   * Beide antworten mit dem ganzen Fahrzeug. Den **Stand** liest die Fläche
   * danach neu: Ob ein Land gedeckt ist, rechnet der Server, und ein Browser,
   * der es aus der Antwort selbst ableitete, wäre die zweite Wahrheit.
   */

  /**
   * Der abgeleitete Stand für diese Reise.
   *
   * Ein 404 heißt: Dieser Server kennt die Vignetten am Fahrzeug noch nicht.
   * Die drei Vignettenzeilen zeigen dann weiter ihr altes Häkchen — wie die
   * Packliste mit ihrer eingebauten Vorlage.
   */
  vignetten: (tripId: number) => request<Vignettenstand>(`/trips/${tripId}/vignetten`),
  /**
   * Der Katalog — Länder, Optionen, Preise, Zugänge.
   *
   * Ohne Reisebezug und deshalb auch ohne Reise abrufbar. Ein 404 heißt: Der
   * Server kennt den Katalog noch nicht; der Dialog fällt dann auf die
   * eingebaute `MAUTREGELN`-Tabelle zurück und bietet nur „Sonstiger
   * Zeitraum" an — Preise erfindet er keine.
   */
  mautkatalog: () => request<Mautkatalog>('/mautkatalog'),
  /**
   * Die Vorschau aufs Gültigkeitsende — **die Rechnung macht der Server**.
   *
   * Vier Regeln hängen daran (Tage, Monate, Dezember-Fenster, rollendes Jahr),
   * und die vierte hat einen Sonderfall, den man im Browser garantiert falsch
   * nachbaut: Beginnt die Reise im Dezember, gehört sie zur Jahresvignette des
   * **Folgejahres**. Deshalb wird gefragt und nicht gerechnet.
   */
  mautGueltigkeit: (land: string, option: string, beginn: string) =>
    request<Mautgueltigkeit>(
      `/mautkatalog/gueltigkeit?land=${encodeURIComponent(land)}` +
        `&option=${encodeURIComponent(option)}&beginn=${encodeURIComponent(beginn)}`
    ),
  /**
   * Einen Posten am Fahrzeug anhängen — der Weg des Knopfs.
   *
   * Eigener Endpunkt und kein PUT: siehe oben. Zehn Zeilen im Server, keine
   * Zusammenführungslogik im Browser.
   */
  vignetteHinterlegen: (fahrzeugId: number, posten: Fahrzeugvignette) =>
    request<Vehicle>(`/vehicles/${fahrzeugId}/vignetten`, {
      method: 'POST',
      body: JSON.stringify(posten)
    }),
  /**
   * Die ganze Liste am Fahrzeug setzen — **nur** `vignetten`.
   *
   * Kein `name` im Körper, und das ist die Stelle, an der der Vertrag genau
   * hinsieht: `PUT /vehicles/{id}` wertet mit `exclude_unset` aus, ein
   * weggelassenes Feld ist also keine Löschung. Den Namen mitzuschicken hieße,
   * ihn aus einer Fläche zu überschreiben, die ihn gar nicht anzeigt.
   */
  fahrzeugVignetten: (fahrzeugId: number, posten: Fahrzeugvignette[]) =>
    request<Vehicle>(`/vehicles/${fahrzeugId}`, {
      method: 'PUT',
      body: JSON.stringify({ vignetten: posten })
    }),

  ladesaeulen: (tripId: number, minKw = 50, spread = 15_000) =>
    request<LadesaeuleDto[]>(
      `/trips/${tripId}/ladesaeulen?min_kw=${minKw}&spread=${spread}&limit=300`
    ),
  /** Vorhersage je Station. Auswertung passiert im Rechenkern, nicht hier. */
  wetter: (tripId: number, tage = 14) =>
    request<{ punkte: { ord: number | null; name: string | null; lat: number; lon: number; wetter: unknown }[] }>(
      `/trips/${tripId}/wetter?tage=${tage}`
    ),

  /**
   * Fahrzeugzustand aus der Smart-Cloud.
   *
   * `alter_s` ist die Zahl, die in die Oberfläche gehört: Die Cloud antwortet
   * langsam und wird zwischengespeichert, also gibt der Server das Alter mit,
   * statt eine Frische zu behaupten. Ist im Konto nichts eingerichtet,
   * antwortet der Server mit 501 — dann bleibt der Knopf weg.
   */
  autoZustand: (frisch = false) =>
    request<AutoZustand>(`/auto/zustand${frisch ? '?frisch=true' : ''}`),

  /** Fahrten aus dem Auto ins Reisetagebuch übernehmen. Wiederholbar. */
  /** Alle Tagebucheinträge einer Reise, neueste zuerst — samt Medien. */
  tagebuch: (tripId: number) =>
    request<Tagebucheintrag[]>(`/trips/${tripId}/journal`),

  /**
   * Was die Reise kostet — geplant, tatsächlich, je Tag, Kategorie und Land.
   *
   * Der Server rechnet das (`kosten.py`), nicht der Browser. Zwei Gründe: Die
   * Ist-Beträge hängen an Tagebucheinträgen, die hier gar nicht alle liegen,
   * und dieselbe Rechnung bedient den Reiter, die CSV-Ausgabe und das
   * Gespräch über MCP. Drei Wege, die jeder für sich summieren, zeigen
   * irgendwann drei Zahlen.
   *
   * Antwortet mit **403**, wenn das Konto den Schalter `tagebuch` nicht hat —
   * die Ist-Beträge *sind* Tagebucheinträge, nur zu Summen verdichtet. Der
   * Text der Absage ist lesbar und gehört in die Fläche; leere Daten wären
   * eine Lüge über eine Reise ohne Ausgaben.
   */
  kosten: (tripId: number) => request<Kostenstand>(`/trips/${tripId}/kosten`),

  /* ── Die Briefings ───────────────────────────────────────────────────
   *
   * Drei Wege, zwei davon rein lesend. Alle drei gehören **Besitzer und
   * Mitplaner** (`_plant_mit` im Backend): Ein Briefing verdichtet nur, was der
   * Aufrufer ohnehin sehen darf — Stationen, Plan, Pässe, Ladeplan.
   *
   * Gerechnet wird ausschließlich auf dem Server (`briefing.py`, reine
   * Funktionen ohne Datenbankzugriff), und zwar aus dem Plan-Schnappschuss.
   * Der Browser fragt hier nach fertigem Text und setzt ihn nicht selbst
   * zusammen: Dieselbe Auskunft bedient den Planer, die App und Lottes
   * Vorlesewerkzeuge — drei Fassungen wären drei Erzählungen über denselben
   * Tag.
   */

  /**
   * Was ist an diesem Tag dran? Struktur und fertiger Text in einer Antwort.
   *
   * **Ohne `datum` ist morgen gemeint** — die Frage, die am Abend gestellt
   * wird. Welcher Tag das ist, entscheidet der Server anhand der Zeitzone
   * dort, wo die Reise gerade steht, und nicht die Uhr dieses Browsers: Um
   * 00:30 mitteleuropäischer Zeit ist in Neuseeland längst der übernächste
   * Tag. Ein hier gerechnetes Datum wäre eine zweite Meinung darüber, was
   * morgen ist — und niemand sähe, an welchen Abenden sie abweicht.
   *
   * Erlaubt sind außerdem die Wörter heute und morgen sowie `JJJJ-MM-TT`;
   * alles andere weist der Server mit 422 ab, statt zu raten. **404** heißt:
   * Es liegt kein Plan auf dem Server oder der Tag gehört nicht zur Reise —
   * beides ist keine Störung, sondern eine Auskunft.
   */
  briefing: (tripId: number, datum?: string) => {
    const p = datum ? `?${new URLSearchParams({ datum })}` : '';
    return request<Tagesbriefing>(`/trips/${tripId}/briefing${p}`);
  },

  /**
   * Was erwartet mich auf dieser Reise? Einmal, vor der Abfahrt.
   *
   * Reisekopf, je Kapitel ein Absatz aus dessen Zusammenfassung und den
   * Warum-Sätzen der wichtigsten Stationen, dazu eine Schlusszeile mit dem,
   * was vor der Abfahrt offen ist.
   *
   * **Ohne übernommene Warum-Sätze bleibt es ein Zahlenkopf**, und das ist
   * ehrlich so: Der Endpunkt erfindet keinen Ersatz für Sätze, die niemand
   * geschrieben hat.
   */
  reisebriefing: (tripId: number) =>
    request<Reisebriefing>(`/trips/${tripId}/briefing/reise`),

  /**
   * Ein Vorschlag für den Warum-Satz einer Station — **erzeugt, nicht
   * gespeichert**.
   *
   * Der einzige Weg dieses Features, an dem ein Sprachmodell im Spiel ist, und
   * er läuft auf Knopfdruck: Die Briefings selbst rechnen nur. Zurück kommt
   * ein Entwurf samt seinen Quellen; in die Spalte kommt er erst, wenn ein
   * Mensch ihn über das gewöhnliche Stationsfeld speichert (`Stop.warum` →
   * `putStops`). `gespeichert: false` steht deshalb in jeder Antwort — ein
   * Client, der es übersieht, zeigte den Entwurf wie einen gespeicherten Wert
   * an, und niemand wüsste, dass da noch ein Knopf fehlt.
   *
   * Adressiert wird über `ord`, die Stelle der Station in der **gespeicherten**
   * Liste — nicht über `stops.id`, die kein `replace_stops` überlebt. Dieselbe
   * Adressierung wie bei den Buchungsbelegen.
   *
   * Fehlschläge kommen mit lesbaren deutschen Sätzen zurück (422 für eine
   * Station ohne Namen oder eine Position, die es nicht gibt; 503, wenn die
   * Recherche nicht möglich war). Sie gehören unverändert in die Fläche.
   */
  warumEntwurf: (tripId: number, ord: number) =>
    request<Warumentwurf>(`/trips/${tripId}/stops/${ord}/warum-entwurf`, {
      method: 'POST'
    }),

  /* ── Das Reisebuch ───────────────────────────────────────────────────
   *
   * Die gefahrene Reise als gesetztes PDF. Beide Wege gehören **nur dem
   * Besitzer**: Der Server prüft `_owned_trip` und antwortet einem Mitplaner
   * mit 404. Das ist Absicht und keine Lücke — das Buch fasst in einer Datei
   * zusammen, was sonst über fünf Ansichten verteilt liegt, und eine
   * heruntergeladene Datei bleibt, auch wenn jemand nicht mehr mitplant. Ob
   * das Panel Erinnern › Reisebuch setzen lässt, entscheidet
   * `reisebuch.zeigtReisebuch`.
   */

  /**
   * Die Vorlagen, aus denen gewählt wird — Name, Titel, ein Satz dazu.
   *
   * Ohne Reisebezug und deshalb ohne Reisekennung: Es ist die Liste des
   * Servers und nicht eine Eigenschaft dieser Reise. Sie wird geholt, wenn das
   * Panel oder die Auswahl aufgeht, und nie beim Laden der Übersicht.
   */
  reisebuchVorlagen: () => request<Vorlage[]>('/reisebuch/vorlagen'),

  /**
   * Das Buch setzen lassen — beide Fassungen, im Hintergrund.
   *
   * Seit dem 7.10.2026 antwortet der Server sofort mit **202** und einem
   * Laufstand `{laeuft, schritt, von, bis, hinweis}`; den Fortgang und am
   * Ende das Buch liefert `reisebuchStand`. Bis dahin lief der Satz geradeaus
   * — bei Reise 19 länger als die 60 Sekunden, nach denen nginx kappt, und
   * der Browser bekam 504, obwohl das Buch entstand. Gelesen wird die Antwort
   * in `reisebuch.standantwort`, die auch die alte Form (der Beizettel mit
   * 201) noch versteht; deshalb `unknown`.
   *
   * Der Körper ist die Form von `ReisebuchWunsch` im Server und wird in
   * `reisebuch.buchkoerper` gebaut, wo ein Test ihn erreicht. Ohne Körper
   * setzt der Server mit seinen Vorgaben (warm, vollständig, ohne Collage).
   * Seit dem 9.10.2026 trägt er dazu die Titelfelder `vorspann`, `titel`
   * und `untertitel` (je höchstens 80 Zeichen); ein leeres Feld geht nicht
   * mit, dann nimmt der Server den Reisenamen.
   *
   * Der POST ersetzt **nichts**: Liegt schon ein Buch, kommt 409. Der Weg zum
   * Neu-Setzen ist `reisebuchEntfernen` und danach dieser Aufruf.
   *
   * Drei Absagen, drei Bedeutungen — auseinandergehalten werden sie in
   * `reisebuch.nachAblehnung` und nicht hier:
   *
   * - **409 mit Objekt** — es liegt schon eines, und das Objekt ist sein
   *   Beizettel. Kein Fehlschlag, sondern eine Auskunft.
   * - **409 mit Satz** — die Reise ist nicht abgeschlossen. Die Oberfläche
   *   zeigt den Knopf ohnehin nur an abgeschlossenen (`zeigtReisebuch`), also
   *   die Ausnahme und nicht der Weg.
   * - **422** — die Vorlage oder die Auswahl kennt der Server nicht.
   *
   * Ein Fehlschlag des Satzes selbst kommt nicht mehr als 503, sondern als
   * `lauf.fehler` im Stand. Und ein **504** ist keine Absage: Das Umschlagbild
   * entsteht noch vor dem Lauf, und dauert es zu lange, kappt nginx — dafür
   * steht `reisebuch.ZEIT_SATZ`, und das Panel sieht danach einmal nach.
   *
   * Alle Sätze gehören unverändert in die Fläche: Sie sind für Menschen
   * geschrieben, und eine eigene Übersetzung wäre eine zweite Wahrheit über
   * dieselbe Regel — dieselbe Abmachung wie bei den Buchungsbelegen.
   */
  reisebuchErzeugen: (tripId: number, koerper?: Reisebuchkoerper | null) =>
    request<unknown>(`/trips/${tripId}/reisebuch`, {
      method: 'POST',
      ...(koerper ? { body: JSON.stringify(koerper) } : {})
    }),

  /**
   * Das liegende Buch wegräumen — der erste Schritt zum Neu-Setzen.
   *
   * Entfernt beide Fassungen und den Beizettel, **nicht** die Umschlagbilder:
   * Die bleiben im Zwischenspeicher des Servers, und der nächste Satz kostet
   * deshalb nichts. 404 heißt: Es lag keines — für den Aufrufer, der danach
   * ohnehin setzt, kein Fehler.
   */
  reisebuchEntfernen: (tripId: number) =>
    request<{ ok: boolean }>(`/trips/${tripId}/reisebuch`, { method: 'DELETE' }),

  /**
   * Der Stand des Buchs und des Laufs, der es setzt: `{buch, lauf}`.
   *
   * `buch` ist der Beizettel des liegenden Buchs — Vorlage, Seiten, Fassungen
   * und die letzte Wahl —, `lauf` der Satzlauf im Hintergrund (seit dem
   * 7.10.2026; vorher war die Antwort der Beizettel allein). Geholt wird er,
   * wenn das Panel oder die Auswahlmaske aufgeht, während eines Laufs alle
   * drei Sekunden, und **nie** beim Laden der Übersicht. 404 heißt: kein Buch
   * und kein Lauf, der etwas zu sagen hätte. Gelesen wird in
   * `reisebuch.standantwort`.
   */
  reisebuchStand: (tripId: number) => request<unknown>(`/trips/${tripId}/reisebuch/stand`),

  /**
   * Das Buch holen und als Objekt-URL zurückgeben.
   *
   * Wortgleiche Abwägung wie bei `anhangHolen`, deshalb derselbe Weg: Diese
   * Anwendung führt keine Sitzung im Cookie. Ein schlichtes `<a href>` auf
   * `/api/trips/…/reisebuch.pdf` schickte den Token nicht mit und bekäme einen
   * 401; ihn in die Adresse zu schreiben hieße, ihn in Zugriffsprotokoll und
   * Browserchronik zu legen, und diese Sitzung gilt ein Jahr.
   *
   * **404 heißt: Es liegt keines.** Das ist keine Störung, sondern die
   * Auskunft — der Aufrufer schiebt seinen Zustand darauf zurück
   * (`reisebuch.nachVerlust`), statt eine Fehlermeldung zu zeigen.
   *
   * **Der Aufrufer muss die URL wieder freigeben** (`URL.revokeObjectURL`).
   */
  /**
   * Alle eigenen Daten als JSON-Datei — und als Objekt-URL zurück.
   *
   * Dieselbe Abwägung wie bei `anhangHolen` und `reisebuchHolen`, deshalb
   * derselbe Weg: Diese Anwendung führt keine Sitzung im Cookie. Ein
   * schlichtes `<a href="/api/me/export">` schickte den Token nicht mit und
   * bekäme einen 401; ihn in die Adresse zu schreiben hieße, ihn in
   * Zugriffsprotokoll und Browserchronik zu legen — und **diese** Adresse
   * liefert das ganze Konto.
   *
   * Was drinsteht, sagt die Fläche (Grundlagen › Konto): Konto ohne
   * Geheimnisse, Fahrzeuge, Reisen mit Stationen, Etappen, Plan, Tagebuch,
   * Anhängen, Freigaben, Tagesplänen. **Medien kommen als Liste mit Links**
   * und nicht als Dateien — ein ZIP mit Fotos ist etwas anderes und steht in
   * Teil 2 des Vertrags.
   *
   * **Der Aufrufer muss die URL wieder freigeben** (`URL.revokeObjectURL`).
   */
  kontoExportHolen: async (): Promise<string> => {
    const token = getToken();
    const res = await fetch('/api/me/export', {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!res.ok) throw await fehlerAus(res);
    return URL.createObjectURL(await res.blob());
  },

  reisebuchHolen: async (tripId: number, fassung: Fassung = 'bildschirm'): Promise<string> =>
    URL.createObjectURL(await api.reisebuchDatei(tripId, fassung)),

  /**
   * Eine Fassung des Reisebuchs als Blob — derselbe Weg wie `reisebuchHolen`,
   * nur ohne Objekt-URL.
   *
   * Seit dem 10.10.2026 (Paket 3, Durchblättern) braucht das Panel die Bytes
   * selbst: Es holt die Bildschirmfassung **einmal** und reicht denselben
   * Blob an die Seitenvorschau und an den Öffnen-Knopf. Ein Buch mit sechzig
   * Fotos sind 40 MB — zweimal laden hieße, auf dem Telefon zweimal warten.
   */
  reisebuchDatei: async (tripId: number, fassung: Fassung = 'bildschirm'): Promise<Blob> => {
    const token = getToken();
    // Die Fassung als Abfrage und nicht als eigener Pfad: Ohne sie liefert der
    // Server die Bildschirmfassung, und genau so holt die App weiter (iOS
    // QuickLook kennt den Parameter nicht und braucht ihn nicht).
    const res = await fetch(`/api/trips/${tripId}/reisebuch.pdf?fassung=${fassung}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!res.ok) throw await fehlerAus(res);
    return res.blob();
  },

  /**
   * Das gezeichnete Titelbild einer Reise, als Objekt-URL.
   *
   * Der Rückfall für Reisen **ohne** Foto — ein bewusst abstraktes SVG, das
   * der Server zeichnet. Ob überhaupt gefragt wird, entscheidet
   * `titelbild.brauchtGezeichnetes`; hier steht nur der Weg dorthin.
   *
   * Drei Dinge, die man vor dem Aufruf wissen muss:
   *
   * - **Der allererste Abruf je Reise dauert 10 bis 20 Sekunden** — der Server
   *   zeichnet dann synchron. Danach kommt das Bild aus seinem Zwischenspeicher.
   *   Ein eigenes Zeitlimit steht deshalb ausdrücklich nicht hier: Es träfe
   *   genau den einen legitimen Fall und ließe die Kachel für immer leer.
   * - **404 heißt nicht kaputt**, sondern: Auf diesem Server ist keine
   *   Bilderzeugung eingerichtet. Kein Fehlerzustand, und die Reise sieht aus
   *   wie vorher.
   * - **503 heißt: gerade nicht** — später vielleicht schon.
   *
   * Nur der Besitzer; an einer fremden Reise wäre es ein 404 je Zeile.
   * **Der Aufrufer muss die URL wieder freigeben** (`URL.revokeObjectURL`).
   */
  titelbildKiHolen: async (tripId: number): Promise<string> => {
    const token = getToken();
    const res = await fetch(`/api/trips/${tripId}/titelbild-ki.svg`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!res.ok) throw await fehlerAus(res);
    return URL.createObjectURL(await res.blob());
  },

  /**
   * Einen Eintrag anlegen.
   *
   * `anreichern` steht auf `true`: Ort, Ladestand, Temperatur und Station
   * ergänzt der Server, sofern die Angaben frisch genug sind. Was er nicht
   * konnte, steht mit Begründung unter `anreicherung` in der Antwort.
   */
  tagebuchSchreiben: (tripId: number, eintrag: Record<string, unknown>) =>
    request<Tagebucheintrag & { anreicherung?: { ergaenzt: string[]; ausgelassen: Record<string, string> } }>(
      `/trips/${tripId}/journal`,
      { method: 'POST', body: JSON.stringify(eintrag) }
    ),

  tagebuchAendern: (tripId: number, id: number, patch: Record<string, unknown>) =>
    request<Tagebucheintrag>(`/trips/${tripId}/journal/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patch)
    }),

  tagebuchEintragLoeschen: (tripId: number, id: number) =>
    request<{ ok: boolean }>(`/trips/${tripId}/journal/${id}`, { method: 'DELETE' }),

  /**
   * Eine Datei an einen Eintrag hängen — als roher Body, kein Formular.
   *
   * Der Server nimmt sie direkt entgegen; `FormData` wäre auf beiden Seiten
   * nur Umweg. Die Art wird aus der Endung erkannt, lässt sich aber setzen.
   */
  tagebuchMediumHochladen: (
    tripId: number,
    eintragId: number,
    datei: File,
    art?: 'foto' | 'video' | 'audio'
  ) => {
    const p = new URLSearchParams({ name: datei.name });
    if (art) p.set('art', art);
    return request<{ medium_id: number; datei: string; art: string }>(
      `/trips/${tripId}/journal/${eintragId}/medien?${p}`,
      {
        method: 'POST',
        body: datei,
        headers: { 'Content-Type': datei.type || 'application/octet-stream' }
      }
    );
  },

  /** Titel und Beschreibung eines Mediums — erscheinen auf der geteilten Seite. */
  tagebuchMediumBeschriften: (
    tripId: number,
    eintragId: number,
    mediumId: number,
    texte: { titel?: string | null; beschreibung?: string | null }
  ) =>
    request<{ ok: boolean }>(
      `/trips/${tripId}/journal/${eintragId}/medien/${mediumId}`,
      { method: 'PATCH', body: JSON.stringify(texte) }
    ),

  /** Eine Webadresse an den Eintrag hängen — Webcam, Artikel, Karte. */
  tagebuchLinkAnlegen: (
    tripId: number,
    eintragId: number,
    daten: { url: string; titel?: string | null; beschreibung?: string | null }
  ) =>
    request<{ id: number }>(`/trips/${tripId}/journal/${eintragId}/links`, {
      method: 'POST',
      body: JSON.stringify(daten)
    }),

  tagebuchLinkLoeschen: (tripId: number, eintragId: number, linkId: number) =>
    request<{ ok: boolean }>(
      `/trips/${tripId}/journal/${eintragId}/links/${linkId}`,
      { method: 'DELETE' }
    ),

  tagebuchMediumLoeschen: (tripId: number, eintragId: number, mediumId: number) =>
    request<{ ok: boolean }>(
      `/trips/${tripId}/journal/${eintragId}/medien/${mediumId}`,
      { method: 'DELETE' }
    ),

  /**
   * Eine Mediendatei holen und als Objekt-URL zurückgeben.
   *
   * ## Warum nicht einfach eine Adresse für `<img src>`
   *
   * Dafür müsste der Sitzungstoken in die Abfragezeichenkette — anders käme
   * er bei einem `<img>` nicht mit. Dort landet er dann in den
   * Zugriffsprotokollen von nginx und in der Browserchronik, und diese
   * Sitzung gilt ein Jahr. Für ein Vorschaubild ist das ein schlechter
   * Tausch.
   *
   * Stattdessen wird die Datei mit `Authorization` geholt und als Blob
   * eingehängt. Der Preis: Ein Video lädt vollständig, bevor es abspielt —
   * über eine Objekt-URL gibt es keine Bereichsabfragen. Für die eigene
   * Durchsicht ist das hinnehmbar; die geteilte Seite bekommt später einen
   * eigenen Weg, weil dort ohnehin keine Sitzung im Spiel ist.
   *
   * **Der Aufrufer muss die URL wieder freigeben** (`URL.revokeObjectURL`),
   * sonst hält der Browser jedes durchgeblätterte Bild im Speicher.
   */
  medienHolen: async (
    tripId: number,
    eintragId: number,
    mediumId: number,
    vorschau = false
  ): Promise<string> => {
    const token = getToken();
    const p = vorschau ? '?vorschau=true' : '';
    const res = await fetch(
      `/api/trips/${tripId}/journal/${eintragId}/medien/${mediumId}${p}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
    if (!res.ok) throw await fehlerAus(res);
    return URL.createObjectURL(await res.blob());
  },

  /** Die Teilen-Links einer Reise. Ohne Adressen — die gibt es nur einmal. */
  freigaben: (tripId: number) => request<Freigabe[]>(`/trips/${tripId}/freigaben`),

  /** Neuen Link erzeugen. Die Adresse steht **einmal** in der Antwort. */
  /** Kurzlebiger Link auf die eigene geteilte Seite — nichts zu merken. */
  vorschau: (tripId: number) =>
    request<{ url: string; gilt_minuten: number }>(`/trips/${tripId}/vorschau`, {
      method: 'POST'
    }),

  freigabeAnlegen: (tripId: number, daten: Record<string, unknown>) =>
    request<{ id: number; url: string; hinweis: string }>(`/trips/${tripId}/freigaben`, {
      method: 'POST',
      body: JSON.stringify(daten)
    }),

  freigabeAendern: (tripId: number, id: number, daten: Record<string, unknown>) =>
    request<{ ok: boolean }>(`/trips/${tripId}/freigaben/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(daten)
    }),

  freigabeWiderrufen: (tripId: number, id: number) =>
    request<{ ok: boolean }>(`/trips/${tripId}/freigaben/${id}`, { method: 'DELETE' }),

  /**
   * Eine **schon zurückgezogene** Freigabe endgültig löschen.
   *
   * Der zweite Schritt nach `freigabeWiderrufen`, nie ein Ersatz dafür: Der
   * Server nimmt nur Zeilen an, die bereits widerrufen sind, und antwortet
   * sonst mit 404 und einem `detail`. So kann ein lebender Link nicht aus
   * Versehen verschwinden — er muss erst sterben, dann kann man ihn
   * wegräumen. Rückmeldungen bleiben, sie verlieren nur den Verweis.
   */
  freigabeEntfernen: (tripId: number, id: number) =>
    request<{ ok: boolean }>(`/trips/${tripId}/freigaben/${id}?endgueltig=true`, {
      method: 'DELETE'
    }),

  /* ── Mitplanen ───────────────────────────────────────────────────────
   *
   * Die Verwaltung gehört dem Besitzer. Für einen Mitplaner antwortet
   * `mitplaner()` mit **404** — und genau das ist die Weiche der Oberfläche:
   * Sie fragt den Server, statt am Client zu raten, wem die Reise gehört. Zwei
   * Orte, die dieselbe Frage beantworten, gingen irgendwann auseinander, und
   * die falsche Antwort wäre hier ein Knopf, der nichts tut.
   */

  mitplaner: (tripId: number) => request<Mitplanerstand>(`/trips/${tripId}/mitplaner`),

  /** Neuen Mitplaner-Link erzeugen. Die Adresse steht **einmal** in der Antwort. */
  mitplanerEinladen: (tripId: number, tage = 7) =>
    request<{ id: number; url: string; hinweis: string }>(`/trips/${tripId}/mitplaner`, {
      method: 'POST',
      body: JSON.stringify({ tage })
    }),

  /** Jemanden von der Reise nehmen. Wirkt sofort. */
  mitplanerEntfernen: (tripId: number, userId: number) =>
    request<{ ok: boolean }>(`/trips/${tripId}/mitplaner/${userId}`, { method: 'DELETE' }),

  /** Einen noch offenen Link entwerten. */
  mitplanerEinladungZuruecknehmen: (tripId: number, einladungId: number) =>
    request<{ ok: boolean }>(`/trips/${tripId}/mitplaner/einladung/${einladungId}`, {
      method: 'DELETE'
    }),

  /** Selbst aufhören mitzuplanen. Der einzige Weg hier, der nicht dem Besitzer gehört. */
  mitplanenAustreten: (tripId: number) =>
    request<{ ok: boolean }>(`/trips/${tripId}/mitplaner/ich`, { method: 'DELETE' }),

  /**
   * Was hinter einem Mitplaner-Link steckt — **ohne Anmeldung**.
   *
   * Der Server antwortet auf alles Unbrauchbare mit demselben 404: abgelaufen,
   * zurückgezogen, eingelöst, erfunden. Die Fläche darf daraus nichts weiter
   * ableiten, als dass der Link nicht mehr gilt.
   */
  mitplanenAnsehen: (token: string) =>
    request<Mitplanenangebot>(`/mitplanen/${encodeURIComponent(token)}`),

  /** Den Link einlösen — angemeldet. Der Besitzer selbst bekommt 409 mit Text. */
  mitplanenEinloesen: (token: string) =>
    request<Mitplanenergebnis>(`/mitplanen/${encodeURIComponent(token)}`, { method: 'POST' }),

  /** Alles, was Mitleser hinterlassen haben — auch das Verborgene. */
  rueckmeldungen: (tripId: number) =>
    request<Rueckmeldung[]>(`/trips/${tripId}/rueckmeldungen`),

  /** Der eine Aufruf für den Punkt am Reiter. */
  rueckmeldungenAnzahl: (tripId: number) =>
    request<{ neu: number }>(`/trips/${tripId}/rueckmeldungen/anzahl`),

  rueckmeldungAendern: (tripId: number, id: number, daten: Record<string, unknown>) =>
    request<{ ok: boolean }>(`/trips/${tripId}/rueckmeldungen/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(daten)
    }),

  rueckmeldungenGelesen: (tripId: number) =>
    request<{ gelesen: number }>(`/trips/${tripId}/rueckmeldungen/gelesen`, {
      method: 'POST'
    }),

  journalAusAuto: (tripId: number) =>
    request<{ gefunden: number; uebernommen: number }>(
      `/trips/${tripId}/journal/aus-auto`,
      { method: 'POST' }
    ),

  /** Verbrauchsfaktor aus dem Reisetagebuch nachziehen. */
  faktorNachziehen: (vehicleId: number, tripId: number) =>
    request<{ faktor_alt: number; faktor_neu: number; km: number; kwh: number; verbrauch_ist: number | null; eintraege: number }>(
      `/vehicles/${vehicleId}/faktor?trip_id=${tripId}`,
      { method: 'POST' }
    ),

  paddleTouren: () =>
    request<{ verfuegbar: boolean; touren: PaddleTour[] }>('/paddle/touren'),

  paddleGewaesser: () =>
    request<{ verfuegbar: boolean; gewaesser: Gewaesser[] }>('/paddle/gewaesser'),

  ladesaeulenAuffrischen: (tripId: number) =>
    request<{ stuetzen: number; gefunden: number }>(
      `/trips/${tripId}/ladesaeulen/auffrischen`,
      { method: 'POST' }
    ),

  /**
   * Echte Straßenumwege zu den **gewählten** Ladestopps.
   *
   * Höchstens acht Paare (der Server antwortet darüber mit 422), und jedes
   * Ergebnis gilt für **eine** Richtung — der Aufrufer verdoppelt, siehe
   * `ladeplan.umwegHinUndZurueck`. Ein Feld zu viel im Körper weist der Server
   * ebenfalls mit 422 ab (`extra=forbid`), damit niemand ein `richtung: beide`
   * mitschickt und die halbe Zahl für die ganze hält.
   *
   * Ausdrücklich nicht für die Kandidatenliste: Die trägt bis zu 300 Säulen,
   * und 300 echte Routen wären ein Sechstel des Tageskontingents für eine
   * Zwischenzahl.
   */
  ladesaeulenUmwege: (tripId: number, paare: { von: [number, number]; nach: [number, number] }[]) =>
    request<{ umwege: ({ distanz_m: number; dauer_ms: number } | null)[] }>(
      `/trips/${tripId}/ladesaeulen/umwege`,
      { method: 'POST', body: JSON.stringify({ paare }) }
    ),

  /* ── Verwaltung ──────────────────────────────────────────────────────
   *
   * Alles hier geht **über Kontogrenzen hinweg** und antwortet mit 403, wenn
   * das angemeldete Konto kein Admin ist. Der Client prüft das nicht vorher:
   * Eine zweite Prüfung im Browser wäre keine zusätzliche Sicherheit, sondern
   * eine zweite Wahrheit, die irgendwann von der ersten abweicht. Was die
   * Oberfläche mit einem 403 macht, entscheidet die Fläche.
   */

  adminNutzer: () => request<AdminNutzer[]>('/admin/nutzer'),

  /**
   * Zustand, Schalter, Kontingent und Notiz eines Kontos.
   *
   * `funktionen` wird vom Server **hineingemischt** und ersetzt nicht:
   * `{ mcp: true }` schaltet MCP ein und lässt alles andere, wie es war. Ein
   * ausdrückliches `null` setzt auf die Vorgabe zurück — der Weg, eine
   * Entscheidung zurückzunehmen statt sie umzudrehen.
   *
   * Die Rolle steht hier bewusst nicht: Der Server antwortet darauf mit 422.
   * Wer sie über diese Fläche vergeben könnte, hätte kein Recht, sondern eine
   * Formalität — sie wird auf dem Server gesetzt (`zugang.py admin`).
   */
  adminNutzerAendern: (
    id: number,
    daten: {
      status?: string;
      funktionen?: Record<string, boolean> | null;
      medien_limit_mb?: number;
      notiz?: string | null;
    }
  ) =>
    request<{ nutzer: AdminNutzer }>(`/admin/nutzer/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(daten)
    }),

  /**
   * Ein Konto restlos entfernen.
   *
   * Zwei Bedingungen des Servers, die die Fläche kennen muss: Das Konto muss
   * **vorher stillgelegt** sein (sonst 409), und `name` muss der Anzeigename
   * oder die E-Mail sein, genau so, wie sie in der Liste stehen (sonst 409).
   * Die Antwort sagt, was weg ist — ein blankes „ok" auf etwas Endgültiges
   * verschweigt genau das, was man in dem Moment wissen will.
   */
  adminNutzerLoeschen: (id: number, name: string) =>
    request<{ konten: number; reisen: number; dateien: number }>(`/admin/nutzer/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ name })
    }),

  adminSitzungen: (id: number) => request<AdminSitzung[]>(`/admin/nutzer/${id}/sitzungen`),

  /** Überall abmelden: Das Konto bleibt, die Geräte fliegen. */
  adminSitzungenLoeschen: (id: number) =>
    request<{ entfernt: number }>(`/admin/nutzer/${id}/sitzungen`, { method: 'DELETE' }),

  adminEinladungen: () => request<AdminEinladung[]>('/admin/einladungen'),

  /** Der Link steht **einmal** in der Antwort und in keiner Liste. */
  adminEinladungAnlegen: (daten: {
    name?: string | null;
    email?: string | null;
    funktionen?: Record<string, boolean> | null;
    medien_limit_mb?: number | null;
    tage?: number;
  }) =>
    request<FrischeEinladung>('/admin/einladungen', {
      method: 'POST',
      body: JSON.stringify(daten)
    }),

  /** Nur offene. Eine eingelöste antwortet mit 409 — das Konto ist da. */
  adminEinladungZuruecknehmen: (id: number) =>
    request<{ id: number; zustand: string }>(`/admin/einladungen/${id}`, {
      method: 'DELETE'
    }),

  adminStatistik: () => request<AdminStatistik>('/admin/statistik')
};

/**
 * Startet den Apple-Login. Voller Redirect, kein Popup — Apple erlaubt kein iframe.
 *
 * `zielpfad` ist der Pfad, auf dem man **nach** der Anmeldung landen soll —
 * Vorgabe die Startseite. Gebraucht wird er von der Mitplaner-Seite: Wer dort
 * ohne Konto ankommt, wäre nach dem Umweg über Apple sonst auf der Reiseliste
 * und müsste den Link aus der Nachricht ein zweites Mal heraussuchen.
 *
 * Die volle Adresse und nicht nur der Pfad, weil `_safe_next` im Backend die
 * **Herkunft** vergleicht (Schema, Host, Port) und alles Fremde auf die
 * Startseite zurückwirft. `location.origin` davorzusetzen ist deshalb keine
 * Höflichkeit, sondern die Bedingung dafür, dass das Ziel überhaupt gilt.
 */
export function startAppleLogin(zielpfad = '/'): void {
  const next = encodeURIComponent(location.origin + zielpfad);
  location.href = `/api/auth/apple/start?next=${next}`;
}
